import type { CartSkuDto } from '../cart/schemas'
import type { PromotionType } from '../pricing'
import type { ProductVariantSchema } from '../product/schemas'
import { ProductSchema } from '../product/schemas'
import type { PlanCategory } from '../subscription'
import type { MealQuantity, Plan } from '../subscription/plan.schemas'
import z from 'zod'
import { Brand, HistoryTables } from '../../shared'
import { generateRequiredPaginationResult } from '../../shared/types'
import { CreateCatBodySchema, CreateDogBodySchema, DogDtoSchema, UpdateDogBodySchema } from '../animal'
import { FeedingPlanType } from '../common'
import { CustomerIdBackfillCursorSchema } from '../fulfilment/schemas'
import { DogActivityLevel, DogBodyType, DogGender, DogLifestage, MigrationCohort } from '../laravel-data-exporter'
import {
  AddressSchema,
  AddressState,
  CreateOrderComplaintInputSchema,
  DeliverySlotWindow,
  JobStatus,
  OrderAnimalServingSchema,
  OrderComplaintSchema,
  OrderDiscountSchema,
  OrderLineItemSchema,
  OrderPricingBackfillCohort,
  OrderResponseSchema,
  PaymentStatus,
  UpdateOrderComplaintInputSchema,
} from '../order'
import { ItemsSchema, PromotionUsageType } from '../pricing'
import { MealRecommendationSchema, ProductType, RecommendationType } from '../product/schemas'
import {
  ExtraSchema,
  FeedingPlanServingSize,
  FeedingPlanStatus,
  MealQuantitySchema,
  SubscriptionStatus,
} from '../subscription'
import { TygaBoxStrategy, TygaMeals } from '../subscription/plan.schemas'
import { FeedingPlanSchema, SubscriptionSchema } from '../subscription/schemas'
import { ActorTypes, GetUserEventQuerySchema, GetUserEventResponseSchema } from '../user-event'

export type InflatedCartSkuDto = Omit<CartSkuDto, 'productVariantId'> & {
  type: ProductType
  variant: ProductVariantSchema
}

export interface InflatedCartBundleDto {
  id: string
  skus: InflatedCartSkuDto[]
}

export interface InflatedCartPromotionDto {
  promotionId: string
  discountId: string | null
  type: PromotionType
  value: string
  isValid: boolean
}

export type InflatedCartLykaPlanDto = Pick<Plan, 'id' | 'slug' | 'name' | 'category' | 'percentOfCalories'> & {
  planId: string
  animalId: string
  products: ProductSchema[]
  totalPrice: number
  discountedTotalPrice?: number
  dailyPrice: number
  discountedDailyPrice?: number
  servingSize: number
  puppyServingSize: number | null
  puppyAdultServingSize: number | null
  pouchSize: number
  mealQuantities: MealQuantity[]
  cadenceInWeeks: number
}

export interface InflatedCartTygaPlanAnimalDto {
  animalId: string
  dailySkus: number
  dailyEnergyKilocalories: number
  minDailyEnergyKilocalories: number
}

export interface InflatedCartTygaPlanDto {
  id: string
  slug: FeedingPlanType
  name: string
  planId: string
  skus: InflatedCartSkuDto[]
  cadenceInWeeks: number
  totalPrice: number
  discountedTotalPrice?: number
  dailyPrice: number
  discountedDailyPrice?: number
  category: PlanCategory
  animals: InflatedCartTygaPlanAnimalDto[]
}

export interface InflatedCartSubscriptionDto {
  id: string
  skus?: InflatedCartSkuDto[]
  bundles?: InflatedCartBundleDto[]
  lykaPlans: Record<string, InflatedCartLykaPlanDto[]>
  tygaPlans: InflatedCartTygaPlanDto[]
}

export interface InflatedCartDto {
  id: string
  userId: string
  skus: InflatedCartSkuDto[]
  bundles: InflatedCartBundleDto[]
  subscriptions: InflatedCartSubscriptionDto[]
  promotions: InflatedCartPromotionDto[]
  totalPrice: number
  discountedTotalPrice?: number
  planTotalPrice: number
  discountedPlanTotalPrice?: number
}

export const TygaCheckoutInputSchema = z.object({
  workflowId: z.string().optional(),
  userId: z.string().uuid(),
  email: z.string().email(),
  phoneNumber: z.string(),
  cartId: z.string().uuid(),
  confirmationTokenId: z.string(),
  expectedDeliverySlotId: z.nativeEnum(DeliverySlotWindow),
  deliveryDate: z.coerce.date(),
  deliveryNote: z.string().optional(),
  userAddress: AddressSchema,
  expectedAmount: z.number().int().nonnegative(),
})

export type TygaCheckoutInput = z.infer<typeof TygaCheckoutInputSchema>

export const TygaCheckoutResultSchema = z.object({
  subscriptions: SubscriptionSchema.array(),
  orders: OrderResponseSchema.array(),
})

export type TygaCheckoutResult = z.infer<typeof TygaCheckoutResultSchema>

export const LykaCheckoutInputSchema = z.object({
  workflowId: z.string().optional(),
  userId: z.string().uuid(),
  cartId: z.string().uuid(),
  confirmationTokenId: z.string(),
  expectedDeliverySlotId: z.nativeEnum(DeliverySlotWindow),
  deliveryDate: z.coerce.date(),
  deliveryNote: z.string().optional(),
  userAddress: AddressSchema,
  expectedAmount: z.number().int().nonnegative(),
})

export type LykaCheckoutInput = z.infer<typeof LykaCheckoutInputSchema>
/** @deprecated - use TygaCheckoutInput instead */
export type CheckoutInput = LykaCheckoutInput

export const LykaCheckoutResultSchema = z.object({
  subscriptions: SubscriptionSchema.array(),
  orders: OrderResponseSchema.array(),
})

export type LykaCheckoutResult = z.infer<typeof LykaCheckoutResultSchema>
/** @deprecated - use LykaCheckoutResult instead */
export type CheckoutResult = LykaCheckoutResult

export const AddressInputSchema = z.object({
  addressId: z.string(),
  address1: z.string(),
  address2: z.string().optional(),
  suburb: z.string(),
  city: z.string(),
  state: z.nativeEnum(AddressState),
  postCode: z.string(),
  countryCode: z.string(),
  deliveryInstructions: z.string().optional(),
  companyName: z.string().optional(),
})

// Match the animal service update contract (same pattern as UpdateCatBodySchema on updateCat).
export const UpdateDogProfileInputSchema = UpdateDogBodySchema

export type UpdateDogProfileInput = z.infer<typeof UpdateDogProfileInputSchema>

const RecommendationMealSchema = z.object({
  type: z.string(),
  sku: z.string(),
  recommended: z.boolean(),
  recommendedType: z.nativeEnum(RecommendationType),
  allergens: z.string().array(),
  illnesses: z.string().array(),
})

export type RecommendationMeal = z.infer<typeof RecommendationMealSchema>

export const UpdateDogProfileResultSchema = z.object({
  dogProfile: DogDtoSchema,
  recommendedDailyServingSizeInGrams: FeedingPlanSchema.shape.currentDailyServingSizeInGrams.optional(),
  recommendedPouchSizeInGrams: FeedingPlanSchema.shape.currentPouchSizeInGrams.optional(),
  subscriptions: SubscriptionSchema.array(),
  recommendationMeals: RecommendationMealSchema.array(),
})

export type UpdateDogProfileResult = z.infer<typeof UpdateDogProfileResultSchema>

export const PricedAnimalPlanInputSchema = z.object({
  animalProducts: z.record(z.string().uuid(), z.string().uuid().array()),
  postcode: z.string(),
})

export type PricedAnimalPlanInput = z.infer<typeof PricedAnimalPlanInputSchema>

export const PricedAnimalPlanSchema = z.object({
  planId: z.string().uuid(),
  planType: z.nativeEnum(FeedingPlanType),
  totalPrice: z.number().int().gt(0),
  planTotalPrice: z.number().int().gt(0),
  baseCadenceInWeeks: z.number().int().gt(0),
  dailyPrice: z.number().int().gt(0),
})

export type PricedAnimalPlan = z.infer<typeof PricedAnimalPlanSchema>

export const TygaPlanPricingInputSchema = z.object({
  planId: z.string().uuid(),
  cadenceInWeeks: z.number().int().gt(0),
  skus: ItemsSchema.array(),
  // ** @deprecated - use discountIds instead */
  discountId: z.string().uuid().optional(),
  discountIds: z.string().uuid().array().optional(),
})

export type TygaPlanPricingInput = z.infer<typeof TygaPlanPricingInputSchema>

export const TygaPlanPricingSchema = z.object({
  totalPrice: z.number().int().gt(0),
  discountedTotalPrice: z.number().int().gt(0),
  dailyPrice: z.number().int().gt(0),
  discountedDailyPrice: z.number().int().gt(0),
})

export type TygaPlanPricing = z.infer<typeof TygaPlanPricingSchema>

export const ReviewUpdateSubscriptionPayloadSchema = z.object({
  subscriptionId: z.string().uuid(),
  version: z.number().int().gte(0),
  cadenceInWeeks: z.number().int().gt(0),
  feedingPlans: z
    .object({
      animalId: z.string().uuid(),
      status: z.nativeEnum(FeedingPlanStatus),
      isEligible: z.boolean().optional(),
      currentDailyServingSizeInGrams: z.nativeEnum(FeedingPlanServingSize),
      currentShippedDailyServingSizeInGrams: z.number().gte(0),
      currentPouchSizeInGrams: z.number().int().gte(0),
      totalPouches: z.number().int().gte(0),
      totalPrice: z.number().int().nonnegative(),
      dailyPrice: z.number().int().nonnegative(),
      skus: z
        .object({
          productVariantId: z.string().uuid(),
          ratio: z.number().int().gte(0),
          price: z.number().int().gte(0),
        })
        .array()
        .min(1),
    })
    .array()
    .min(1),
  feedingPlanTotalPouches: z.number().int().gt(0),
  feedingPlanTotalPrice: z.number().int().gt(0),
})

export const CancelSubscriptionInputSchema = z.object({
  subscriptionId: z.string().uuid(),
  primaryCancelReasonId: z.string().uuid(),
  secondaryCancelReasonId: z.string().uuid().optional(),
  otherCancelReason: z.string().optional(),
  cancellingUserId: z.string().uuid().describe('The user id of the person cancelling the subscription'),
  cancelledByAdmin: z.boolean().optional().default(false),
  workflowId: z.string().optional(),
})

export type CancelSubscriptionInput = z.infer<typeof CancelSubscriptionInputSchema>

export const CancelSubscriptionResultSchema = z.object({
  subscriptionId: z.string().uuid(),
  deletedOrderIds: z.string().uuid().array(),
  cancelledOrderIds: z.string().uuid().array(),
})
export type CancelSubscriptionResult = z.infer<typeof CancelSubscriptionResultSchema>

export const PauseSubscriptionInputSchema = z.object({
  subscriptionId: z.string().uuid(),
  pauseReasonId: z.string().uuid().optional(),
  otherPauseReason: z.string().optional(),
  pausedByAdmin: z.boolean().optional().default(false),
  workflowId: z.string().optional(),
})

export type PauseSubscriptionInput = z.infer<typeof PauseSubscriptionInputSchema>

export const PauseSubscriptionResultSchema = z.object({
  subscriptionId: z.string().uuid(),
  deletedOrderIds: z.string().uuid().array(),
  cancelledOrderIds: z.string().uuid().array(),
})
export type PauseSubscriptionResult = z.infer<typeof PauseSubscriptionResultSchema>

export const AddAnimalToSubscriptionInputSchema = z.object({
  animal: CreateDogBodySchema,
  subscriptionId: z.string().uuid(),
  productIds: z.string().uuid().array(),
})

export type AddAnimalToSubscriptionInput = z.infer<typeof AddAnimalToSubscriptionInputSchema>

export const AddAnimalToSubscriptionResultSchema = z.object({
  animal: DogDtoSchema,
  subscription: SubscriptionSchema,
})

export type AddAnimalToSubscriptionResult = z.infer<typeof AddAnimalToSubscriptionResultSchema>

export const ManualChargeOrderInputSchema = z.object({
  orderId: z.string().uuid(),
})

export type ManualChargeOrderInput = z.infer<typeof ManualChargeOrderInputSchema>

export const ManualChargeOrderResultSchema = z.object({
  order: OrderResponseSchema,
  payment: z
    .object({
      id: z.string(),
      providerPaymentId: z.string().nullable(),
      amount: z.number(),
      status: z.string(),
      provider: z.string(),
    })
    .optional(),
})

export type ManualChargeOrderResultSchema = z.infer<typeof ManualChargeOrderResultSchema>

export const GenerateFutureOrderInputSchema = z.object({
  workflowId: z.string().optional(),
  subscriptionId: z.string().uuid(),
  isCheckout: z.boolean().optional().describe('If true, the orders are being generated for a checkout'),
  startDateOverride: z
    .object({
      startDate: z.string().date(),
      timezone: z.string(),
    })
    .optional()
    .describe('Manual start date in case of reset future order'),
  resetDeliveryDates: z.boolean().optional().describe('Reset all future orders delivery date from default start date'),
  dryRun: z.boolean().optional().describe('If true, will not create orders but return the generated orders'),
  minOrdersRequired: z
    .number()
    .min(1)
    .optional()
    .describe('Ensures that there is a minimum number of pending orders exist'),
  rafPromotions: z
    .array(
      z.object({
        discountId: z.string().uuid(),
        promotionUserId: z.string().uuid(),
        promotionType: z.custom<PromotionType>(),
        promotionValue: z.string().min(1).max(250),
      }),
    )
    .optional()
    .describe(
      'RAF promotion metadata to attach to matching order discounts; used at checkout where this data is in-memory but not persisted on the subscription discount',
    ),
})

export type GenerateFutureOrderInput = z.infer<typeof GenerateFutureOrderInputSchema>

export const RestructureOrdersInputSchema = z.object({
  subscriptionId: z.string().uuid(),
})

export const CreateTestAccountInputSchema = z.object({
  emailPrefix: z.string(),
  address: AddressInputSchema.omit({
    addressId: true,
    countryCode: true,
  }),
  deliveryDate: z.string().date(),
  deliverySlot: z.nativeEnum(DeliverySlotWindow).default(DeliverySlotWindow.AM),
  dogs: CreateDogBodySchema.array().min(1),
  applySignupPromotion: z
    .boolean()
    .or(z.string())
    .optional()
    .describe(
      'When true, applies the hardcoded signup promotion to the test account. When set to a string, applies that specific promotion.',
    ),
})

export type CreateTestAccountInput = z.infer<typeof CreateTestAccountInputSchema>

export const CreateTygaTestAccountInputSchema = z.object({
  emailPrefix: z.string(),
  address: AddressInputSchema.omit({
    addressId: true,
    countryCode: true,
  }),
  deliveryDate: z.string().date(),
  deliverySlot: z.nativeEnum(DeliverySlotWindow).default(DeliverySlotWindow.AM),
  cats: CreateCatBodySchema.array().min(1),
})

export type CreateTygaTestAccountInput = z.infer<typeof CreateTygaTestAccountInputSchema>

/**
 * Input for provisioning the canonical Tyga e2e customer. Resolves the user by
 * email; if absent it self-provisions one (lead with the given `cognitoUserId`,
 * address, then promotes to TygaCustomer via checkout and sets `password`) so the
 * full-stack local e2e no longer depends on a pre-existing shared-auth user. When
 * the user already exists it only seeds the local subscription/order data, keyed
 * on the user's canonical id. Local/e2e seeding only.
 */
export const CreateTygaAccountForExistingUserInputSchema = z.object({
  email: z.string().email(),
  firstName: z.string().optional().default('Test'),
  lastName: z.string().optional().default('Account'),
  // Cognito user id to provision when the user is absent. Required for the self-provision path.
  cognitoUserId: z.string().uuid().optional(),
  // Password set after checkout. Under hermetic AUTH_LOCAL_MINT sign-in ignores it,
  // but it keeps the user complete and matches E2E_TYGA_CUSTOMER_PASSWORD.
  password: z.string().optional(),
  // Fixed phone for the canonical customer (defaults to a generated one). The tyga
  // account specs assert this exact number, so it must be deterministic.
  phoneNumber: z.string().optional(),
  // Mark the first charged order delivered so the customer reads as an established
  // subscriber. The delivery-date "Send ASAP" option is hidden until the first
  // order (currentActiveOrders[0], nth 0) has fulfilmentStatus 'delivered'.
  markFirstOrderDelivered: z.boolean().optional().default(false),
  address: AddressInputSchema.omit({
    addressId: true,
    countryCode: true,
  }),
  deliveryDate: z.string().date(),
  deliverySlot: z.nativeEnum(DeliverySlotWindow).default(DeliverySlotWindow.AM),
  cats: CreateCatBodySchema.array().default([]),
})

export type CreateTygaAccountForExistingUserInput = z.infer<typeof CreateTygaAccountForExistingUserInputSchema>

export const CreateTestAccountResultSchema = z.object({
  userId: z.string().uuid(),
  email: z.string().email(),
  password: z.string(),
})

export type CreateTestAccountResult = z.infer<typeof CreateTestAccountResultSchema>

const TestAccountScenarioEnum = z.enum([
  'delivery-today',
  'delivery-soon',
  'delivery-recent',
  'delivery-past',
  'payment-today',
  'payment-soon',
  'payment-failed',
  'subscription-cancelled',
  'subscription-paused',
  'with-removed-pet',
])

export type TestAccountScenario = z.infer<typeof TestAccountScenarioEnum>

/**
 * Stages of fulfilment processing for test account orders.
 *
 * - `manifested` — Shipment created in courier service + fulfilment job at BACKLOG in fulfilment service
 * - `packing-queued` — Fulfilment jobs advanced to QUEUED (packing station assigned)
 * - `packing-in-progress` — Fulfilment jobs advanced to IN_PROGRESS (boxes being packed)
 * - `packed` — Fulfilment jobs advanced to PACKED (all boxes packed, awaiting palletisation)
 * - `palletised` — Fulfilment jobs advanced to PALLETISED (loaded onto pallet for dispatch)
 */
export const FulfilmentStageEnum = z.enum([
  'manifested',
  'packing-queued',
  'packing-in-progress',
  'packed',
  'palletised',
])

export type FulfilmentStage = z.infer<typeof FulfilmentStageEnum>

export const CreateAccountScenarioInputSchema = z.object({
  emailPrefix: z.string(),
  scenario: TestAccountScenarioEnum.default('delivery-soon'),
  multipleDogs: z.boolean().default(false),
  hasPuppy: z
    .boolean()
    .default(false)
    .describe('When true, the first dog is created as a 6-week-old puppy (3kg, 5cm).'),
  applySignupPromotion: z
    .boolean()
    .or(z.string())
    .default(true)
    .describe(
      'When true, applies the hardcoded signup promotion to the test account. When set to a string, applies that specific promotion.',
    ),
  fulfilmentStage: FulfilmentStageEnum.optional().describe(
    'Sets up fulfilment data (shipment + jobs) for the first order. Maps to JobStatus in the fulfilment service.',
  ),
})

export type CreateAccountScenarioInput = z.infer<typeof CreateAccountScenarioInputSchema>

export const MoveSubscriptionExtraInputSchema = z.object({
  workflowId: z.string().optional(),
  subscriptionId: z.string().uuid(),
  extraId: z.string().uuid(),
  startOrderId: z.string().uuid(),
})

export const AddSubscriptionExtraInputSchema = z.object({
  workflowId: z.string().optional(),
  subscriptionId: z.string().uuid(),
  extraType: z.nativeEnum(ProductType),
  productVariantId: z.string().uuid(),
  startOrderId: z.string().uuid(),
  nthOrder: z.number().int().gte(1),
  quantity: z.number().int().gte(1),
})

export const UpdateSubscriptionExtraInputSchema = z.object({
  workflowId: z.string().optional(),
  subscriptionId: z.string().uuid(),
  extraId: z.string().uuid(),
  quantity: z.number().int().gte(1),
})

export const DeleteSubscriptionExtraInputSchema = z.object({
  workflowId: z.string().optional(),
  subscriptionId: z.string().uuid(),
  extraId: z.string().uuid(),
  cancelReasonId: z.string().uuid().optional(),
  cancelNotes: z.string().optional(),
  cancelledByAdmin: z.boolean().optional(),
})

export const AddOneOffExtraInputSchema = z.object({
  workflowId: z.string().optional(),
  subscriptionId: z.string().uuid(),
  orderId: z.string().uuid(),
  extras: z
    .object({
      productVariantId: z.string().uuid(),
      quantity: z.number().int().gte(1),
    })
    .array()
    .min(1),
  /**
   * When true (default), any existing recurring subscription extra for the
   * same product variant is cancelled before the one-off is added. CCA passes
   * `false` so a one-off extra can coexist with the recurring subscription
   * extra on the same order.
   *
   * TODO: once CA and CCA both pass this flag explicitly, make it required
   * and remove the optional fallback in the orchestration service.
   */
  deleteSubscriptionExtras: z.boolean().optional(),
})

export const DeleteOneOffExtraInputSchema = z.object({
  workflowId: z.string().optional(),
  subscriptionId: z.string().uuid(),
  orderId: z.string().uuid(),
  orderLineItemId: z.string().uuid(),
})

export const UpdateOneOffExtraInputSchema = z.object({
  workflowId: z.string().optional(),
  subscriptionId: z.string().uuid(),
  orderId: z.string().uuid(),
  orderLineItemId: z.string().uuid(),
  quantity: z.number().int().gte(1),
})

/**
 * The charge/refund a plan change implies for the already-paid (PROCESSED) order, in integer cents.
 * Returned by the preview so the UI can show the customer what applying to their paid box will cost;
 * `action` mirrors the sign of `differenceCents` ('none' when nothing moves).
 */
export const PaidOrderAdjustmentSchema = z.object({
  orderId: z.string().uuid(),
  deliveryDate: z.string().date(),
  currentChargeCents: z.number().int(),
  newTotalChargeCents: z.number().int(),
  newTotalPriceCents: z.number().int(),
  // The meal-plan portion of the re-priced order, persisted to order.planTotalPrice / planTotalCharge
  // when the adjustment is applied. Optional for backward compatibility with callers that predate
  // plan-total tracking.
  newPlanTotalPriceCents: z.number().int().optional(),
  newPlanTotalChargeCents: z.number().int().optional(),
  differenceCents: z.number().int(),
  action: z.enum(['charge', 'refund', 'none']),
  // True when `action` is 'none' but the paid box should still be re-boxed. Set only for a puppy
  // serving-size change: a puppy feeding plan is priced off `max(puppyServingSize,
  // puppyAdultServingSize)`, so moving the puppy serving size while it sits below the adult figure
  // changes the pouches shipped (and the per-animal servings) without moving a cent. CCA offers
  // "paid and future orders" on those, and the apply skips the charge/refund and just re-boxes.
  // Every other zero-delta edit stays false and keeps the plain no-op it has always had.
  canApplyWithoutPayment: z.boolean().optional(),
})

export type PaidOrderAdjustment = z.infer<typeof PaidOrderAdjustmentSchema>

export const UpdateOrderExtrasInputSchema = z.object({
  workflowId: z.string().optional(),
  orderId: z.string().uuid(),
  subscriptionId: z.string().uuid(),
  oneOffExtras: z.array(
    z.object({
      productVariantId: z.string().uuid(),
      quantity: z.number().int().gte(1),
      discountId: z.string().uuid().nullable().optional(),
    }),
  ),
  // When true and the targeted order is the already-paid (PROCESSED) box, charge/refund
  // the price difference of the edit (apply only; ignored by preview).
  applyToPaidOrder: z.boolean().optional(),
  /**
   * When true (default), any existing recurring subscription extras whose
   * product variants are being added as one-offs are cancelled. CCA passes
   * `false` so one-off extras can coexist with the recurring subscription
   * extras on the targeted order.
   *
   * TODO: once CA and CCA both pass this flag explicitly, make it required
   * and remove the optional fallback in the orchestration service.
   */
  deleteSubscriptionExtras: z.boolean().optional(),
})
export type UpdateOrderExtrasInput = z.infer<typeof UpdateOrderExtrasInputSchema>
export const UpdateOrderExtrasOutputSchema = OrderResponseSchema
export type UpdateOrderExtrasOutput = z.infer<typeof UpdateOrderExtrasOutputSchema>

export const UpdateOrderExtrasPreviewInputSchema = UpdateOrderExtrasInputSchema
const OneOffExtraPreviewItemSchema = z.object({
  productVariantId: z.string().uuid(),
  quantity: z.number().int().gte(1),
  price: z.number().int().nonnegative(),
})

export const UpdateOrderExtrasPreviewOutputSchema = z.object({
  /**
   * @deprecated Use `orderLineItems` to render the hypothetical order
   * contents. Will be removed once all consumers (CA, CCA) have migrated.
   */
  oneOffExtras: z.array(OneOffExtraPreviewItemSchema),
  // TODO: remove optional from orderLineItems once consumers have migrated.
  orderLineItems: z.array(OrderLineItemSchema).optional(),
  mealsTotal: z.number().int().nonnegative(),
  mealsTotalCharge: z.number().int().nonnegative(),
  extrasTotal: z.number().int().nonnegative(),
  extrasTotalCharge: z.number().int().nonnegative(),
  orderTotal: z.number().int().nonnegative(),
  orderTotalCharge: z.number().int().nonnegative(),
  // Present when the targeted order is the already-paid (PROCESSED) box and still editable:
  // the charge/refund this edit implies if applied with `applyToPaidOrder`.
  paidOrderAdjustment: PaidOrderAdjustmentSchema.optional(),
})

const ProductItemChangeSchema = z.object({
  action: z.enum(['updated', 'added', 'removed']),
  product_id: z.union([z.number(), z.string()]),
  order_id: z.string().uuid().optional(),
  line_item_id: z.string().uuid(),
  animal_id: z.union([z.number(), z.string()]).optional(),
  bundle_id: z.string().uuid().optional(),
  extra_id: z.string().uuid().optional(),
  quantity: z
    .union([
      z.object({
        old: z.union([z.number(), z.string()]),
        new: z.union([z.number(), z.string()]),
      }),
      z.union([z.number(), z.string()]),
    ])
    .optional(),
})

export type ProductItemChange = z.infer<typeof ProductItemChangeSchema>

export const PostPaymentOrderModifiedSchema = z.object({
  name: z.literal('post_payment_order_modified'),
  key: z.string(),
  data: z.object({
    orderId: z.string(),
    changeSet: z
      .object({
        delivery_date: z
          .object({
            old: z.string(),
            new: z.string(),
          })
          .optional(),
        make_date: z
          .object({
            old: z.string().nullable(),
            new: z.string().nullable(),
          })
          .optional(),
        delivery_slot: z
          .object({
            old: z.string(),
            new: z.string(),
          })
          .optional(),
        dispatch_date_cutoff: z
          .object({
            old: z.string(),
            new: z.string(),
          })
          .optional(),
        manifest_date_cutoff: z
          .object({
            old: z.string(),
            new: z.string(),
          })
          .optional(),
        hold_status: z
          .object({
            old: z.boolean(),
            new: z.boolean(),
          })
          .optional(),
        fulfilment_status: z
          .object({
            old: z.nativeEnum(JobStatus),
            new: z.nativeEnum(JobStatus),
          })
          .optional(),
        inventory_items: z
          .object({
            action: z.enum(['updated', 'added', 'removed']).optional(),
            order_id: z.union([z.number(), z.string()]).optional(),
            animal_id: z.union([z.number(), z.string()]).optional(),
            pouches_total: z
              .object({
                old: z.number(),
                new: z.number(),
              })
              .optional(),
            daily_grams: z
              .object({
                old: z.number(),
                new: z.number(),
              })
              .optional(),
            total_chicken: z
              .object({
                old: z.number(),
                new: z.number(),
              })
              .optional(),
            total_beef: z
              .object({
                old: z.number(),
                new: z.number(),
              })
              .optional(),
            total_lamb: z
              .object({
                old: z.number(),
                new: z.number(),
              })
              .optional(),
            total_turkey: z
              .object({
                old: z.number(),
                new: z.number(),
              })
              .optional(),
            total_fish: z
              .object({
                old: z.number(),
                new: z.number(),
              })
              .optional(),
            total_kangaroo: z
              .object({
                old: z.number(),
                new: z.number(),
              })
              .optional(),
            total_goat: z
              .object({
                old: z.number(),
                new: z.number(),
              })
              .optional(),
          })
          .optional(),
        inventory_additions: z
          .object({
            action: z.enum(['updated', 'added', 'removed']).optional(),
            order_id: z.union([z.number(), z.string()]).optional(),
            animal_id: z.union([z.number(), z.string()]).optional(),
            chicken: z.object({ old: z.number(), new: z.number() }).optional(),
            beef: z.object({ old: z.number(), new: z.number() }).optional(),
            lamb: z.object({ old: z.number(), new: z.number() }).optional(),
            turkey: z.object({ old: z.number(), new: z.number() }).optional(),
            fish: z.object({ old: z.number(), new: z.number() }).optional(),
            kangaroo: z.object({ old: z.number(), new: z.number() }).optional(),
            goat: z.object({ old: z.number(), new: z.number() }).optional(),
          })
          .optional(),
        product_items: z
          .object({
            added_count: z.number().int().nonnegative().optional(),
            removed_count: z.number().int().nonnegative().optional(),
            updated_count: z.number().int().nonnegative().optional(),
            total_quantity_change: z
              .object({
                old: z.number().int().nonnegative(),
                new: z.number().int().nonnegative(),
              })
              .optional(),
            affected_line_item_ids: z.array(z.string().uuid()).optional(),
            changes: z.array(ProductItemChangeSchema).optional(),
          })
          .optional(),
        delivery_address_routing: z
          .object({
            depot_code: z.object({ old: z.string(), new: z.string() }),
            facility_id: z.object({ old: z.number(), new: z.number() }),
            postcode: z.object({ old: z.string(), new: z.string() }),
          })
          .optional(),
      })
      .catchall(z.any()),
    metadata: z.object({
      timestamp: z.string(),
      source: z.enum(['cdc_migration', 'order_service']),
      payment_date: z.string().optional(),
    }),
  }),
})

export type PostPaymentOrderModified = z.infer<typeof PostPaymentOrderModifiedSchema>

const OrderCancelledChangeSetSchema = z
  .object({
    make_date: z
      .object({
        old: z.string(),
        new: z.string().nullable(),
      })
      .optional(),
  })
  .catchall(z.any())

export const OrderCancelledSchema = z.object({
  name: z.literal('order_cancelled'),
  key: z.string(),
  data: z.object({
    orderId: z.string(),
    changeSet: OrderCancelledChangeSetSchema,
    metadata: z.object({
      timestamp: z.string(),
      source: z.literal('order_service'),
      payment_date: z.string(),
    }),
  }),
})

export type OrderCancelledEvent = z.infer<typeof OrderCancelledSchema>

export const MigrateDataFromLaravelToPhoenixQuerySchema = z.object({
  workflowExecutionTimeout: z.string().describe('Override execution timeout eg. `15 minute`').optional(),
})

export const MigrateDataFromLaravelToPhoenixInputSchema = z.object({
  bulkRunId: z
    .string()
    .optional()
    .describe(
      "Whole-run id (the bulk mother workflow's firstExecutionRunId) when invoked from a bulk migration; correlates every child of one run across continue-as-new generations.",
    ),
  email: z.string().email(),
  dryRun: z.coerce.boolean().optional(),
})

export type MigrateDataFromLaravelToPhoenixInput = z.infer<typeof MigrateDataFromLaravelToPhoenixInputSchema>

export const MigrateUserEventDataFromLaravelToPhoenixInputSchema = z.object({
  bulkRunId: z
    .string()
    .optional()
    .describe(
      "Whole-run id (the bulk mother workflow's firstExecutionRunId) when invoked from a bulk migration; correlates every child of one run across continue-as-new generations.",
    ),
  email: z.string().email(),
  startDate: z.string().date().optional().describe('Date in YYYY-MM-DD format'),
  endDate: z.string().date().optional().describe('Date in YYYY-MM-DD format, if not provided will take today date'),
  dryRun: z.coerce.boolean().optional(),
})

export type MigrateUserEventDataFromLaravelToPhoenixInput = z.infer<
  typeof MigrateUserEventDataFromLaravelToPhoenixInputSchema
>

export const MigrateGeneralOutputSchema = z.object({
  success: z.boolean(),
})

export const HistoryTableResultStatusEnum = z.enum(['success', 'skipped', 'failed'])

export type HistoryTableResultStatus = z.infer<typeof HistoryTableResultStatusEnum>

export const MigrateHistoryDataFromLaravelToPhoenixInputSchema = z.object({
  email: z.string().email(),
  dryRun: z.coerce.boolean().optional(),
  selectedHistoryTables: z
    .nativeEnum(HistoryTables)
    .array()
    .default(Object.values(HistoryTables))
    .describe('Subset of history tables to migrate. Defaults to all implemented tables.'),
  verifyHistoryData: z
    .boolean()
    .default(true)
    .describe(
      'Whether to perform data verification after migration. If true, will compare migrated data with original data and return any mismatches.',
    ),
})

export type MigrateHistoryDataFromLaravelToPhoenixInput = z.infer<
  typeof MigrateHistoryDataFromLaravelToPhoenixInputSchema
>

export const MigrateHistoryDataFromLaravelToPhoenixOutputSchema = z.object({
  animal: z.object({
    dogStatusHistory: MigrateGeneralOutputSchema,
    dogDeactivationReason: MigrateGeneralOutputSchema,
  }),
  subscription: z.object({
    feedingPlanHistory: MigrateGeneralOutputSchema,
    subscriptionCancellationHistory: MigrateGeneralOutputSchema,
    subscriptionPauseReasons: MigrateGeneralOutputSchema,
  }),
})

export type MigrateHistoryDataFromLaravelToPhoenixOutput = z.infer<
  typeof MigrateHistoryDataFromLaravelToPhoenixOutputSchema
>

/** Dry-run preview: migrated Laravel price vs Phoenix pricing-engine recalculation. */
export const MigrationSubscriptionPricePreviewSchema = z.object({
  status: z.enum(['unchanged', 'changed', 'skipped']),
  migratedPrice: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Feeding-plan total (cents) that migration would write'),
  calculatedPrice: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Feeding-plan total (cents) from the Phoenix pricing service'),
  reason: z.string().optional().describe('Present when status is skipped'),
})

export type MigrationSubscriptionPricePreview = z.infer<typeof MigrationSubscriptionPricePreviewSchema>

export const MigrateDataFromLaravelToPhoenixOutputSchema = z.object({
  bulkRunId: z
    .string()
    .optional()
    .describe('Echoes the input bulkRunId for correlating this child result with its bulk run.'),
  exporter: MigrateGeneralOutputSchema,
  animal: MigrateGeneralOutputSchema,
  subscription: MigrateGeneralOutputSchema,
  payment: MigrateGeneralOutputSchema,
  userAddress: MigrateGeneralOutputSchema,
  discounts: MigrateGeneralOutputSchema,
  historyData: MigrateHistoryDataFromLaravelToPhoenixOutputSchema,
})

export type MigrateDataFromLaravelToPhoenixOutput = z.infer<typeof MigrateDataFromLaravelToPhoenixOutputSchema>

export const BulkMigrateDataFromLaravelToPhoenixInputSchema = z.object({
  emails: z.string().email().array(),
  cohort: z.nativeEnum(MigrationCohort).optional(),
  cohortLimit: z.number().int().gt(0).lte(5000).default(1000).optional(),
  dryRun: z.coerce.boolean().optional(),
})

export type BulkMigrateDataFromLaravelToPhoenixInput = z.infer<typeof BulkMigrateDataFromLaravelToPhoenixInputSchema>

export const BulkMigrateDataFromLaravelToPhoenixV2InputSchema = BulkMigrateDataFromLaravelToPhoenixInputSchema.extend({
  // V2 accepts the email list either inline (emails) or from an S3 object
  // (emailsS3Uri), so emails is optional here. Exactly one source is enforced by
  // the refine below.
  emails: z.string().email().array().optional(),
  // Alternative to inline emails[]: a plain-text S3 object (comma/space/newline
  // separated) read once at workflow start and baked into the run input. Lets a
  // run carry far more emails than is comfortable to inline in the start payload.
  emailsS3Uri: z
    .string()
    .url()
    .optional()
    .describe('s3://bucket/key plain-text email list. Alternative to inline emails[]; ignored when cohort is set.'),
  // Alternative source: a plain-text S3 object of Laravel user IDs. Resolved to
  // emails (and legacy IDs, so non-dry runs can set per-user migration status)
  // via the exporter once at workflow start. Lets a run reuse the same user-ID
  // file the lyka-migrations batch scripts consume.
  userIdsS3Uri: z
    .string()
    .url()
    .optional()
    .describe(
      's3://bucket/key plain-text Laravel user-ID list. Resolved to emails via the exporter; ignored when cohort is set.',
    ),
  // Cap is bounded by the BulkMigration worker's sticky cache, not by appetite.
  // Rule of thumb: maxCachedWorkflows >= ~4x concurrency (each child also keeps
  // its grandchildren live). Current worker = 1 task, 4GB, maxCachedWorkflows 256,
  // so ~64 is the safe ceiling; above it workflows evict and force-replay under
  // fan-out (and ~75 OOMs the 4GB container). Raise this ONLY together with worker
  // count/memory + maxCachedWorkflows (see apps/orchestration/src/config.ts
  // TaskQueues.BulkMigration).
  concurrency: z
    .number()
    .int()
    .gt(0)
    .lte(64)
    .optional()
    .describe(
      'Number of child migrations kept in-flight at once. Higher = faster but more downstream (exporter/DB) load. Defaults to 50 when omitted; max 64 is bounded by worker cache/memory.',
    ),
})
  .refine(
    (input) => [input.cohort, input.emails?.length, input.emailsS3Uri, input.userIdsS3Uri].filter(Boolean).length >= 1,
    { message: 'Provide one of: cohort, emails, emailsS3Uri, or userIdsS3Uri.' },
  )
  .refine((input) => [input.emails?.length, input.emailsS3Uri, input.userIdsS3Uri].filter(Boolean).length <= 1, {
    message: 'Provide only one email source: emails, emailsS3Uri, or userIdsS3Uri.',
  })

export type BulkMigrateDataFromLaravelToPhoenixV2Input = z.infer<
  typeof BulkMigrateDataFromLaravelToPhoenixV2InputSchema
>

export const BulkMigrateUserEventDataFromLaravelToPhoenixInputSchema = z.object({
  emails: z.string().email().array(),
  startDate: z.string().date().optional().describe('Date in YYYY-MM-DD format'),
  endDate: z.string().date().optional().describe('Date in YYYY-MM-DD format, if not provided will take today date'),
  dryRun: z.coerce.boolean().optional(),
  concurrency: z
    .number()
    .int()
    .gt(0)
    .lte(64)
    .optional()
    .describe('Number of child migrations kept in-flight at once. Defaults to 50 when omitted; max 64.'),
})

export type BulkMigrateUserEventDataFromLaravelToPhoenixInput = z.infer<
  typeof BulkMigrateUserEventDataFromLaravelToPhoenixInputSchema
>

export const BulkMigrateHistoryDataFromLaravelToPhoenixInputSchema = z.object({
  emails: z.string().email().array(),
  dryRun: z.coerce.boolean().optional(),
  selectedHistoryTables: z
    .nativeEnum(HistoryTables)
    .array()
    .default(Object.values(HistoryTables))
    .describe('Subset of history tables to migrate. Defaults to all implemented tables.'),
  verifyHistoryData: z
    .boolean()
    .default(true)
    .describe(
      'Whether to perform data verification after migration. If true, will compare migrated data with original data and return any mismatches.',
    ),
  concurrency: z
    .number()
    .int()
    .gt(0)
    .lte(64)
    .optional()
    .describe('Number of child migrations kept in-flight at once. Defaults to 50 when omitted; max 64.'),
})

export type BulkMigrateHistoryDataFromLaravelToPhoenixInput = z.infer<
  typeof BulkMigrateHistoryDataFromLaravelToPhoenixInputSchema
>

export const BackfillFeedingPlanHistoryIdsInputSchema = z.object({
  dryRun: z
    .boolean()
    .optional()
    .default(false)
    .describe('Resolve and count without writing anything — no updates and no review rows.'),
  batchSize: z
    .number()
    .int()
    .min(1)
    .max(20000)
    .optional()
    .default(2000)
    .describe('History rows scanned per batch. Each batch is one short transaction in the subscription service.'),
})

export type BackfillFeedingPlanHistoryIdsInput = z.infer<typeof BackfillFeedingPlanHistoryIdsInputSchema>

export const BackfillFeedingPlanHistoryPhaseTotalsSchema = z.object({
  scanned: z.number().int(),
  inScope: z.number().int(),
  updated: z.number().int(),
  unresolved: z.number().int(),
  batches: z.number().int(),
})

export const BackfillFeedingPlanHistoryIdsOutputSchema = z.object({
  dryRun: z.boolean(),
  feedingPlan: BackfillFeedingPlanHistoryPhaseTotalsSchema,
  feedingPlanSku: BackfillFeedingPlanHistoryPhaseTotalsSchema,
  nullDanglingFeedingPlan: BackfillFeedingPlanHistoryPhaseTotalsSchema,
  nullDanglingFeedingPlanSku: BackfillFeedingPlanHistoryPhaseTotalsSchema,
  totalUpdated: z.number().int().describe('Rows repaired by the two resolution phases'),
  totalNulled: z
    .number()
    .int()
    .describe('Rows set to NULL by the two null_dangling_* phases — placeholders with no resolvable target'),
  totalUnresolved: z
    .number()
    .int()
    .describe('Rows recorded in feeding_plan_history_backfill_review for manual follow-up'),
})

export type BackfillFeedingPlanHistoryIdsOutput = z.infer<typeof BackfillFeedingPlanHistoryIdsOutputSchema>

/** A migration failure paired with its root-cause message for the run report. */
export const FailedMigrationRecordSchema = z.object({
  email: z.string().email(),
  reason: z.string().describe('Root-cause message of the failure'),
})

export type FailedMigrationRecord = z.infer<typeof FailedMigrationRecordSchema>

export const BulkMigrateDataFromLaravelToPhoenixOutputSchema = z.object({
  numberOfEmails: z.number().int(),
  successEmails: z.string().email().array(),
  // Union of every email that errored. Excludes `alreadyMigratedEmails` (a no-op skip).
  failedEmails: z.string().email().array(),
  // Categorised breakdown of the run. Populated by the v2 bulk workflow; absent
  // for the v1 / user-event / revert endpoints that share this schema.
  alreadyMigratedEmails: z
    .string()
    .email()
    .array()
    .optional()
    .describe('Runs skipped because the user was already migrated'),
  skippedEmails: FailedMigrationRecordSchema.array()
    .optional()
    .describe(
      'Runs deliberately skipped by a business rule (order in flight / legacy puppy algorithm / subscription price change), paired with the reason',
    ),
  failedMigrationEmails: FailedMigrationRecordSchema.array()
    .optional()
    .describe('Runs that failed during migration; the run reverted any partial data, so the user is NOT migrated'),
})

export type BulkMigrateDataFromLaravelToPhoenixOutput = z.infer<typeof BulkMigrateDataFromLaravelToPhoenixOutputSchema>

export const MigrateBaBCustomerFromLaravelToPhoenixInputSchema = z.object({
  email: z
    .string()
    .email()
    .describe('Passed to `getLegacyUserData`; must match the authenticated caller where enforced upstream.'),
})

export type MigrateBaBCustomerFromLaravelToPhoenixInput = z.infer<
  typeof MigrateBaBCustomerFromLaravelToPhoenixInputSchema
>

export const MigrateBaBCustomerFromLaravelToPhoenixOutputSchema = MigrateGeneralOutputSchema.extend({
  workflowId: z.string(),
})

export type MigrateBaBCustomerFromLaravelToPhoenixOutput = z.infer<
  typeof MigrateBaBCustomerFromLaravelToPhoenixOutputSchema
>

export const VerificationDataMismatchSchema = z.object({
  path: z.string(),
  expected: z.string().nullable(),
  actual: z.string().nullable(),
})

export type VerificationDataMismatch = z.infer<typeof VerificationDataMismatchSchema>

export const VerificationResponseSchema = z.object({
  success: z.boolean(),
  userId: z.string().uuid(),
  mismatches: VerificationDataMismatchSchema.array(),
})

export type VerificationResponse = z.infer<typeof VerificationResponseSchema>

export const ApplySubscriptionDiscountInput = z.object({
  userId: z.string().uuid(),
  discountId: z.string().uuid(),
  createdBy: z.string().uuid().describe('The user id of whom is applying the discount for audit'),
})

export type ApplySubscriptionDiscountInput = z.infer<typeof ApplySubscriptionDiscountInput>

export const ApplySubscriptionDiscountResult = z.object({
  affectedOrderIds: z.string().uuid().array(),
})

export type ApplySubscriptionDiscountResult = z.infer<typeof ApplySubscriptionDiscountResult>

export const ApplySubscriptionDiscountPreviewInputSchema = z.object({
  discountId: z
    .string()
    .uuid()
    .optional()
    .describe('The discount to preview. If not provided, returns current prices without any discount.'),
  subscriptionId: z.string().uuid(),
})

export type ApplySubscriptionDiscountPreviewInput = z.infer<typeof ApplySubscriptionDiscountPreviewInputSchema>

export const ApplySubscriptionDiscountPreviewCategorySchema = z.object({
  total: z.number().int().nonnegative().describe('Original total price'),
  discountedTotal: z.number().int().nonnegative().describe('Discounted total price'),
  discountAmount: z.number().int().nonnegative().describe('Amount discounted'),
})

export type ApplySubscriptionDiscountPreviewCategory = z.infer<typeof ApplySubscriptionDiscountPreviewCategorySchema>

export const ApplySubscriptionDiscountPreviewResultSchema = z.object({
  subscriptionId: z.string().uuid(),
  discountId: z.string().uuid().optional().describe('The discount that was previewed, if any'),
  userId: z.string().uuid(),
  meals: ApplySubscriptionDiscountPreviewCategorySchema.describe('Meals (feeding plans) pricing'),
  treats: ApplySubscriptionDiscountPreviewCategorySchema.describe('Treats pricing'),
  supplements: ApplySubscriptionDiscountPreviewCategorySchema.describe('Supplements pricing'),
  priceTotal: z.number().int().nonnegative().describe('Original total subscription price'),
  discountedPriceTotal: z.number().int().nonnegative().describe('Discounted total subscription price'),
  discountAmount: z.number().int().nonnegative().describe('Total amount discounted'),
})

export type ApplySubscriptionDiscountPreviewResult = z.infer<typeof ApplySubscriptionDiscountPreviewResultSchema>

export const RemoveSubscriptionDiscountInputSchema = z.object({
  subscriptionId: z.string().uuid(),
  discountId: z.string().uuid().describe('The discount template ID to remove from the subscription'),
  deletedBy: z.string().uuid().describe('The user id of whom is removing the discount for audit'),
})

export type RemoveSubscriptionDiscountInput = z.infer<typeof RemoveSubscriptionDiscountInputSchema>

export const RemoveSubscriptionDiscountResultSchema = z.object({
  affectedOrderIds: z.string().uuid().array(),
})

export type RemoveSubscriptionDiscountResult = z.infer<typeof RemoveSubscriptionDiscountResultSchema>

export const ApplyOrderDiscountPreviewInputSchema = z.object({
  orderId: z.string().uuid(),
  discountId: z
    .string()
    .uuid()
    .optional()
    .describe('The discount to preview. If not provided, returns current prices without any discount.'),
})
export type ApplyOrderDiscountPreviewInput = z.infer<typeof ApplyOrderDiscountPreviewInputSchema>

export const ApplyOrderDiscountInputSchema = ApplyOrderDiscountPreviewInputSchema.extend({
  discountId: z.string().uuid(),
  createdBy: z.string().uuid().describe('The user id of whom is applying the discount for audit'),
})
export type ApplyOrderDiscountInput = z.infer<typeof ApplyOrderDiscountInputSchema>

export const ApplyOrderDiscountPreviewOutputSchema = ApplySubscriptionDiscountPreviewResultSchema.extend({
  orderId: z.string().uuid(),
})
export type ApplyOrderDiscountPreviewOutput = z.infer<typeof ApplyOrderDiscountPreviewOutputSchema>

export const ApplyOrderDiscountOutputSchema = z.object({
  orderId: z.string().uuid(),
  success: z.boolean(),
})
export type ApplyOrderDiscountOutput = z.infer<typeof ApplyOrderDiscountOutputSchema>

export const RemoveOrderDiscountInputSchema = z.object({
  orderId: z.string().uuid(),
  discountId: z.string().uuid().describe('The discount template ID to remove from the subscription'),
  deletedBy: z.string().uuid().describe('The user id of whom is removing the discount for audit'),
})
export type RemoveOrderDiscountInput = z.infer<typeof RemoveOrderDiscountInputSchema>

export const RemoveOrderDiscountOutputSchema = z.object({
  orderId: z.string().uuid(),
  success: z.boolean(),
})
export type RemoveOrderDiscountOutput = z.infer<typeof RemoveOrderDiscountOutputSchema>

export const DeleteOrderComplaintInputSchema = z.object({
  complaintId: z.string().uuid().describe('The order complaint ID to soft-delete'),
})
export type DeleteOrderComplaintInput = z.infer<typeof DeleteOrderComplaintInputSchema>

export const DeleteOrderComplaintOutputSchema = z.object({
  complaintId: z.string().uuid(),
  success: z.boolean(),
})
export type DeleteOrderComplaintOutput = z.infer<typeof DeleteOrderComplaintOutputSchema>

export const CreateOrderComplaintWorkflowInputSchema = CreateOrderComplaintInputSchema.extend({
  orderId: z.string().uuid(),
})
export type CreateOrderComplaintWorkflowInput = z.infer<typeof CreateOrderComplaintWorkflowInputSchema>

export const CreateOrderComplaintWorkflowOutputSchema = OrderComplaintSchema
export type CreateOrderComplaintWorkflowOutput = z.infer<typeof CreateOrderComplaintWorkflowOutputSchema>

export const UpdateOrderComplaintWorkflowInputSchema = UpdateOrderComplaintInputSchema.extend({
  complaintId: z.string().uuid(),
})
export type UpdateOrderComplaintWorkflowInput = z.infer<typeof UpdateOrderComplaintWorkflowInputSchema>

export const UpdateOrderComplaintWorkflowOutputSchema = OrderComplaintSchema
export type UpdateOrderComplaintWorkflowOutput = z.infer<typeof UpdateOrderComplaintWorkflowOutputSchema>

export const ApplyReferrerRewardInputSchema = z.object({
  promotionUserId: z.string().uuid(),
  brand: z.nativeEnum(Brand),
})

export type ApplyReferrerRewardInput = z.infer<typeof ApplyReferrerRewardInputSchema>

export const ApplyReferrerRewardOutputSchema = z.object({})

export type ApplyReferrerRewardOutput = z.infer<typeof ApplyReferrerRewardOutputSchema>

export const ReactivateSubscriptionInputSchema = z.object({
  userId: z.string().uuid(),
  deliveryDate: z.string().date(),
  preferredDeliverySlotId: z.nativeEnum(DeliverySlotWindow).optional(),
})

export type ReactivateSubscriptionInput = z.infer<typeof ReactivateSubscriptionInputSchema>

export const ResumeSubscriptionInputSchema = z.object({
  subscriptionId: z.string().uuid(),
  deliveryDate: z.string().date(),
  preferredDeliverySlotId: z.nativeEnum(DeliverySlotWindow).optional(),
})

export type ResumeSubscriptionInput = z.infer<typeof ResumeSubscriptionInputSchema>

export const CheckSubscriptionValidityInputSchema = z.object({
  subscriptionId: z.string().uuid(),
  /** Validate against this start date instead of the subscription's own `startAt`. */
  startDateOverride: z.string().date().optional(),
  /** Validate cutoffs against this slot instead of the subscription's `preferredDeliverySlotId`. */
  deliverySlotOverride: z.nativeEnum(DeliverySlotWindow).optional(),
})

export type CheckSubscriptionValidityInput = z.infer<typeof CheckSubscriptionValidityInputSchema>

export const CheckSubscriptionValidityOutputSchema = z.object({
  subscription: SubscriptionSchema,
  timezone: z.string(),
})

export type CheckSubscriptionValidityOutput = z.infer<typeof CheckSubscriptionValidityOutputSchema>

export const VerifySubscriptionOrdersSchema = z.object({
  workflowId: z.string().optional(),
  userId: z.string().uuid(),
  startDate: z.string().date(),
})

export type VerifyOrders = z.infer<typeof VerifySubscriptionOrdersSchema>

export const PreviewDogSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  dailyServingSize: z.number().int().nonnegative(),
  shippedDailyServingSize: z.number().nonnegative(),
  puppyServingSize: z.number().int().nullable(),
  puppyAdultServingSize: z.number().int().nullable(),
  pouchSize: z.number().int().nonnegative(),
  totalPouches: z.number().int().nonnegative(),
})

export const PreviewCatSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  dailyPouches: z.number().nonnegative(),
  pouchSize: z.number().int().nonnegative(),
  totalPouches: z.number().nonnegative(),
})

export const PreviewSubscriptionSchema = z.object({
  id: z.string().uuid(),
  cadence: z.number().int().nonnegative(),
  feedingPlanTotalPouches: z.number().int().nonnegative(),
  feedingPlanTotalPrice: z.number().int().nonnegative(),
  feedingPlanType: z.union([z.literal('full_bowl'), z.literal('half_bowl')]),
})

export const PreviewSubscriptionExtraSchema = z.object({
  id: z.string().uuid().optional(),
  version: z.number().int().gte(0).optional(),
  type: ExtraSchema.shape.type,
  productVariantId: z.string().uuid(),
  price: z.number().int().nonnegative(),
  startOrderIndex: z.number().int().nonnegative(),
  nthOrder: z.number().int().nonnegative(),
  quantity: z.number().int().nonnegative(),
})

export const AddDogToSubscriptionInputSchema = z.object({
  workflowId: z.string().optional(),
  subscriptionId: z.string().uuid(),
  userId: z.string().uuid(),
  dogToAdd: CreateDogBodySchema.extend({
    name: CreateDogBodySchema.shape.name.unwrap().unwrap(),
    gender: CreateDogBodySchema.shape.gender.unwrap().unwrap(),
    weight: CreateDogBodySchema.shape.weight.unwrap().unwrap(),
    bodyType: CreateDogBodySchema.shape.bodyType.unwrap().unwrap(),
    activityLevel: CreateDogBodySchema.shape.activityLevel.unwrap().unwrap(),
    eatingHabits: CreateDogBodySchema.shape.eatingHabits.unwrap().unwrap(),
  }),
  preferredMeals: MealQuantitySchema.shape.type.array().min(1),
  dogRecommendationOverride: z
    .object({
      recommendedDailyServingSizeInGrams: z.number().int().nonnegative(),
    })
    .optional(),
  puppyRecommendationOverride: z
    .object({
      recommendedPuppyServingSize: z.number().int().describe(''),
      recommendedPuppyAdultServingSize: z.number().int(),
    })
    .optional(),
  applyToPaidOrder: z.boolean().optional(),
})
export type AddDogToSubscriptionInput = z.infer<typeof AddDogToSubscriptionInputSchema>

export const AddDogToSubscriptionPreviewDogSchema = PreviewDogSchema.extend({
  toAdd: z.boolean(),
})
export type AddDogToSubscriptionPreviewDog = z.infer<typeof AddDogToSubscriptionPreviewDogSchema>

export const AddDogToSubscriptionPreviewOutputSchema = z.object({
  mealRecommendations: z
    .object({
      productName: z.string(),
      productCode: z.string(),
      recommendationType: MealRecommendationSchema.shape.recommendationType,
      healthIssues: z
        .object({
          name: z.string(),
          slug: z.string(),
        })
        .array(),
      allergies: z
        .object({
          name: z.string(),
          slug: z.string(),
        })
        .array(),
    })
    .array(),
  feedingPlanRecommendation: z.object({
    cadence: z.number().int().nonnegative(),
    planType: FeedingPlanSchema.shape.type,
    dailyServingSize: z.number().int().nonnegative(),
    puppyServingSize: z.number().int().nullable(),
    puppyAdultServingSize: z.number().int().nullable(),
    pouchSize: z.number().int().nonnegative(),
    totalPouches: z.number().int().nonnegative(),
  }),
  current: z.object({
    dogs: AddDogToSubscriptionPreviewDogSchema.array(),
    subscription: PreviewSubscriptionSchema,
    subscriptionExtras: PreviewSubscriptionExtraSchema.array(),
  }),
  toUpdate: z.object({
    dogs: AddDogToSubscriptionPreviewDogSchema.array(),
    subscription: PreviewSubscriptionSchema,
    subscriptionExtras: PreviewSubscriptionExtraSchema.array(),
  }),
  subscriptionExtras: PreviewSubscriptionExtraSchema.array(),
  paidOrderAdjustment: PaidOrderAdjustmentSchema.optional(),
  paidOrderLineItems: z.array(OrderLineItemSchema).optional(),
  paidOrderAnimalServings: z.array(OrderAnimalServingSchema).optional(),
  // When the next upcoming paid order exists but is not eligible for a paid-order adjustment,
  // a human-readable reason so the UI can force "future orders only" and explain why.
  paidOrderIneligibleReason: z.string().optional(),
})
export type AddDogToSubscriptionPreviewOutput = z.infer<typeof AddDogToSubscriptionPreviewOutputSchema>

export const AddDogToSubscriptionOutputSchema = z.object({
  dog: DogDtoSchema,
  subscription: SubscriptionSchema,
  updatedOrderIds: z.string().uuid().array(),
})
export type AddDogToSubscriptionOutput = z.infer<typeof AddDogToSubscriptionOutputSchema>

// Recalculate Meal Plan Subscription
export const RecalculateDogMealPlanSubscriptionInputSchema = z.object({
  // 1). Dog recommendation fields (required and must be provided by the caller) [API: /ts-rest/animals/dogs/recommendation/generate]
  activityLevel: z.nativeEnum(DogActivityLevel),
  bodyType: z.nativeEnum(DogBodyType),
  gender: z.nativeEnum(DogGender),
  weight: z.number().gt(0),
  ageWeeks: z.number().nonnegative().optional(),
  breeds: z.string().array().max(2).optional(),
  // Forwarded to the animal-service getDogRecommendation activity to force a lifestage outcome.
  // See GetDogRecommendationBodySchema for the PUPPY/ADULT semantics.
  overriddenLifestage: z.nativeEnum(DogLifestage).optional(),
  // 2). Plan generation fields [API: /plans/generate]: Using placeholder animal id and meal since we only want to get the serving size, no preferred meals are required from API consumer level
  // 3). Meal recommendation fields [API: /products/recommend-meals]
  allergens: z.string().array(),
  healthIssues: z.string().array(),
})

export const NotRecommendedRecipeSchema = z.object({
  condition: z.string().array(),
  avoid: z.string().array(),
})

export type NotRecommendedRecipe = z.infer<typeof NotRecommendedRecipeSchema>

export const RecipeSchema = z.object({
  name: z.string(),
  slug: z.string(),
})

export type Recipe = z.infer<typeof RecipeSchema>

export const RecipesSchema = z.object({
  recommended: RecipeSchema.array(),
  notRecommended: RecipeSchema.array(),
})

export type Recipes = z.infer<typeof RecipesSchema>

export const PuppyWeightMilestoneSchema = z.object({
  label: z.string(),
  fromWeightKg: z.number().int(),
  toWeightKg: z.number().int(),
  servingSize: z.number().int().gt(0),
})

export type PuppyWeightMilestone = z.infer<typeof PuppyWeightMilestoneSchema>

export const PuppyComedownMilestoneSchema = z.object({
  label: z.string().optional(), // TODO - DUC - Set back to required in next change
  month: z.number().int().gt(0),
  servingSize: z.number().int().gt(0),
})

export type PuppyComedownMilestone = z.infer<typeof PuppyComedownMilestoneSchema>

export const PuppyMilestonesSchema = z.object({
  weightMilestones: PuppyWeightMilestoneSchema.array(),
  comedownMilestones: PuppyComedownMilestoneSchema.array(),
})

export type PuppyMilestones = z.infer<typeof PuppyMilestonesSchema>

export const RecalculateDogMealPlanSubscriptionOutputSchema = z.object({
  recommendedServingSize: z.number().int().gt(0),
  recommendedPuppyServingSize: z.number().int().nullable(),
  recommendedPuppyAdultServingSize: z.number().int().nullable(),
  lifestage: z.nativeEnum(DogLifestage).optional(),
  servingSizes: z.number().int().gt(0).array(),
  notRecommendedMeals: NotRecommendedRecipeSchema.array(),
  recipes: RecipesSchema,
  puppyMilestones: PuppyMilestonesSchema.nullable(),
})

export type RecalculateDogMealPlanSubscriptionOutput = z.infer<typeof RecalculateDogMealPlanSubscriptionOutputSchema>

// Get Puppy Milestones
export const GetPuppyMilestonesInputSchema = z.object({
  dogId: z.string().uuid(),
})

export type GetPuppyMilestonesInput = z.infer<typeof GetPuppyMilestonesInputSchema>

export const GetPuppyMilestonesOutputSchema = PuppyMilestonesSchema

export type GetPuppyMilestonesOutput = z.infer<typeof GetPuppyMilestonesOutputSchema>

// Get Order Allowed Free Pouch Variants
export const GetOrderAllowedFreePouchVariantsInputSchema = z.object({
  orderId: z.string().uuid(),
})

export type GetOrderAllowedFreePouchVariantsInput = z.infer<typeof GetOrderAllowedFreePouchVariantsInputSchema>

export const GetOrderAllowedFreePouchVariantsOutputSchema = ProductSchema.array()

export type GetOrderAllowedFreePouchVariantsOutput = z.infer<typeof GetOrderAllowedFreePouchVariantsOutputSchema>

// Graduate Puppy
export const GraduatePuppyInputSchema = z.object({
  workflowId: z.string().optional(),
  dogId: z.string().uuid(),
})

export type GraduatePuppyInput = z.infer<typeof GraduatePuppyInputSchema>

// Skip/precondition cases throw a non-retryable ApplicationFailure rather than
// returning a failure shape; the happy path just confirms success.
export const GraduatePuppyOutputSchema = z.object({
  success: z.literal(true),
})

export type GraduatePuppyOutput = z.infer<typeof GraduatePuppyOutputSchema>

// Remove Dog from Subscription
export const RemoveDogFromSubscriptionInputSchema = z.object({
  workflowId: z.string().optional(),
  dogId: z.string().uuid(),
  dogDeactivateReasons: z.object({
    primaryReasonId: z.string().uuid(),
    secondaryReasonId: z.string().uuid().optional(),
    otherReason: z.string().optional(),
    actorType: z.enum(ActorTypes).optional().default('system'),
  }),
  subscriptionId: z.string().uuid(),
  subscriptionCancelReasons: z
    .object({
      primaryCancelReasonId: z.string().uuid(),
      secondaryCancelReasonId: z.string().uuid().optional(),
      otherCancelReason: z.string().optional(),
      cancellingUserId: z.string().uuid(),
      cancelledByAdmin: z.boolean(),
    })
    .optional(),
  applyToPaidOrder: z.boolean().optional(),
})
export type RemoveDogFromSubscriptionInput = z.infer<typeof RemoveDogFromSubscriptionInputSchema>

export const RemoveDogFromSubscriptionPreviewDogSchema = PreviewDogSchema.extend({
  toRemove: z.boolean(),
})
export type RemoveDogFromSubscriptionPreviewDog = z.infer<typeof RemoveDogFromSubscriptionPreviewDogSchema>

export const RemoveDogFromSubscriptionPreviewOutputSchema = z.object({
  current: z.object({
    dogs: RemoveDogFromSubscriptionPreviewDogSchema.array(),
    subscription: PreviewSubscriptionSchema,
    subscriptionExtras: PreviewSubscriptionExtraSchema.array(),
  }),
  toUpdate: z.object({
    dogs: RemoveDogFromSubscriptionPreviewDogSchema.array(),
    subscription: PreviewSubscriptionSchema,
    subscriptionExtras: PreviewSubscriptionExtraSchema.array(),
  }),
  subscriptionExtras: PreviewSubscriptionExtraSchema.array(),
  paidOrderAdjustment: PaidOrderAdjustmentSchema.optional(),
  paidOrderLineItems: z.array(OrderLineItemSchema).optional(),
  paidOrderAnimalServings: z.array(OrderAnimalServingSchema).optional(),
  paidOrderIneligibleReason: z.string().optional(),
})
export type RemoveDogFromSubscriptionPreviewOutput = z.infer<typeof RemoveDogFromSubscriptionPreviewOutputSchema>

export const RemoveDogFromSubscriptionOutputSchema = z.object({
  dog: DogDtoSchema,
  subscription: SubscriptionSchema,
  subscriptionCancelled: z.boolean(),
  deletedOrderIds: z.string().uuid().array(),
  cancelledOrderIds: z.string().uuid().array(),
  updatedOrderIds: z.string().uuid().array(),
})
export type RemoveDogFromSubscriptionOutput = z.infer<typeof RemoveDogFromSubscriptionOutputSchema>

// Reactivate Dog from Subscription
export const ReactivateDogFromSubscriptionInputSchema = z.object({
  workflowId: z.string().optional(),
  dogId: z.string().uuid(),
  subscriptionId: z.string().uuid(),
  applyToPaidOrder: z.boolean().optional(),
})
export type ReactivateDogFromSubscriptionInput = z.infer<typeof ReactivateDogFromSubscriptionInputSchema>

export const ReactivateDogFromSubscriptionPreviewDogSchema = PreviewDogSchema.extend({
  toReactivate: z.boolean(),
})
export type ReactivateDogFromSubscriptionPreviewDog = z.infer<typeof ReactivateDogFromSubscriptionPreviewDogSchema>

export const ReactivateDogFromSubscriptionPreviewOutputSchema = z.object({
  current: z.object({
    dogs: ReactivateDogFromSubscriptionPreviewDogSchema.array(),
    subscription: PreviewSubscriptionSchema,
    subscriptionExtras: PreviewSubscriptionExtraSchema.array(),
  }),
  toUpdate: z.object({
    dogs: ReactivateDogFromSubscriptionPreviewDogSchema.array(),
    subscription: PreviewSubscriptionSchema,
    subscriptionExtras: PreviewSubscriptionExtraSchema.array(),
  }),
  subscriptionExtras: PreviewSubscriptionExtraSchema.array(),
  paidOrderAdjustment: PaidOrderAdjustmentSchema.optional(),
  paidOrderLineItems: z.array(OrderLineItemSchema).optional(),
  paidOrderIneligibleReason: z.string().optional(),
})
export type ReactivateDogFromSubscriptionPreviewOutput = z.infer<
  typeof ReactivateDogFromSubscriptionPreviewOutputSchema
>

export const ReactivateDogFromSubscriptionOutputSchema = z.object({
  dog: DogDtoSchema,
  subscription: SubscriptionSchema,
  updatedOrderIds: z.string().uuid().array(),
})
export type ReactivateDogFromSubscriptionOutput = z.infer<typeof ReactivateDogFromSubscriptionOutputSchema>

// Apply Promotion to Cart
export const ApplyPromotionToCartInputSchema = z.object({
  cartId: z.string().uuid(),
  promotionCode: z.string(),
  brand: z.nativeEnum(Brand),
  usageType: z.nativeEnum(PromotionUsageType),
})

export type ApplyPromotionToCartInput = z.infer<typeof ApplyPromotionToCartInputSchema>

export const ApplyPromotionToCartResultSchema = z.object({
  promotionId: z.string().uuid(),
  discountId: z.string().uuid().nullable(),
  cartId: z.string().uuid(),
})

export type ApplyPromotionToCartResult = z.infer<typeof ApplyPromotionToCartResultSchema>

export const UpdateDogSubscriptionActiveFeedingPlansInputSchema = z.object({
  workflowId: z.string().optional(),
  subscriptionId: z.string().uuid(),
  feedingPlanType: z.union([z.literal('full_bowl'), z.literal('half_bowl')]),
  activeFeedingPlans: z
    .object({
      id: z.string().uuid(),
      servingSize: z.number().int().nonnegative(),
      puppyServingSize: z.number().int().nonnegative().optional(),
      puppyAdultServingSize: z.number().int().nonnegative().optional(),
      mealQuantities: MealQuantitySchema.omit({ sku: true }).array().min(1),
    })
    .array()
    .min(1),
  customDeliveries: z
    .object({
      customCadenceInWeeks: z.number().int().gt(0),
      customBoxInWeeks: z.number().int().gt(0),
    })
    .optional(),
  // When true, also charge/refund the difference on the already-paid order (apply only; ignored by preview).
  applyToPaidOrder: z.boolean().optional(),
})

export type UpdateDogSubscriptionActiveFeedingPlansInput = z.infer<
  typeof UpdateDogSubscriptionActiveFeedingPlansInputSchema
>

export const UpdateDogSubscriptionActiveFeedingPlansPreviewDogSchema = PreviewDogSchema.extend({
  name: z.string(),
  notRecommendedMeals: z
    .object({
      name: z.string(),
      slug: z.string(),
      allergens: z.string().array().default([]),
      illnesses: z.string().array().default([]),
    })
    .array(),
  feedingPlan: z.object({
    id: z.string().uuid(),
    servingSize: z.number().int().nonnegative(),
    shippedServingSize: z.number().nonnegative(),
    puppyServingSize: z.number().int().nullable(),
    puppyAdultServingSize: z.number().int().nullable(),
    pouchSize: z.number().int().nonnegative(),
    totalPouches: z.number().int().nonnegative(),
    mealQuantities: MealQuantitySchema.omit({ sku: true }).array().min(1),
  }),
})

export type UpdateDogSubscriptionActiveFeedingPlansPreviewDog = z.infer<
  typeof UpdateDogSubscriptionActiveFeedingPlansPreviewDogSchema
>

export const UpdateDogSubscriptionActiveFeedingPlansPreviewOutputSchema = z.object({
  byPlanType: z
    .object({
      planType: z.union([z.literal('full_bowl'), z.literal('half_bowl')]),
      cadence: z.number().int().nonnegative(),
      totalPrice: z.number().int().nonnegative(),
      dailyPrice: z.number().int().nonnegative(),
      dogs: z
        .object({
          id: z.string().uuid(),
          name: z.string(),
          totalPouches: z.number().int().nonnegative(),
        })
        .array(),
    })
    .array()
    .min(2)
    .max(2),
  current: z.object({
    dogs: UpdateDogSubscriptionActiveFeedingPlansPreviewDogSchema.array().min(1),
    subscription: PreviewSubscriptionSchema,
    subscriptionExtras: PreviewSubscriptionExtraSchema.array(),
  }),
  toUpdate: z.object({
    dogs: UpdateDogSubscriptionActiveFeedingPlansPreviewDogSchema.array().min(1),
    subscription: PreviewSubscriptionSchema,
    subscriptionExtras: PreviewSubscriptionExtraSchema.array(),
  }),
  // Present only when an upcoming paid order exists; lets the UI offer "paid and future orders".
  paidOrderAdjustment: PaidOrderAdjustmentSchema.optional(),
})

export type UpdateDogSubscriptionActiveFeedingPlansPreviewOutput = z.infer<
  typeof UpdateDogSubscriptionActiveFeedingPlansPreviewOutputSchema
>

export const UpdateDogSubscriptionActiveFeedingPlansOutputSchema = z.object({
  subscription: SubscriptionSchema,
  updatedOrderIds: z.string().uuid().array(),
})
export type UpdateDogSubscriptionActiveFeedingPlansOutput = z.infer<
  typeof UpdateDogSubscriptionActiveFeedingPlansOutputSchema
>

export const TygaFeedingPlanTypeSchema = z.enum([FeedingPlanType.tyga_full_bowl, FeedingPlanType.tyga_mixed_bowl])

export type TygaFeedingPlanType = z.infer<typeof TygaFeedingPlanTypeSchema>

export const CatPlanInputSchema = z.object({
  id: z.string().uuid(),
  skusPerDay: z.number().nonnegative().optional(),
  dailyEnergyKilocalories: z.number().int().nonnegative().optional(),
  minDailyEnergyKilocalories: z.number().int().positive().optional(),
  healthIssues: z.string().array().optional(),
  allergies: z.string().array().optional(),
})

export type CatPlanInput = z.infer<typeof CatPlanInputSchema>

export const UpdateCatSubscriptionPlanInputSchema = z.object({
  subscriptionId: z.string().uuid(),
  cadence: z.number().int().positive().optional(),
  planType: TygaFeedingPlanTypeSchema.optional(),
  boxStrategy: z.nativeEnum(TygaBoxStrategy).optional(),
  meals: z
    .object({
      productCode: z.nativeEnum(TygaMeals),
      quantity: z.number().int().nonnegative(),
    })
    .array()
    .optional(),
  animals: CatPlanInputSchema.array().min(1).optional(),
  workflowId: z.string().optional(),
})

export type UpdateCatSubscriptionPlanInput = z.infer<typeof UpdateCatSubscriptionPlanInputSchema>

const CatSubscriptionPreviewAnimalSchema = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
  skusPerDay: z.number().nonnegative(),
  recommendedSkusPerDay: z.number().nonnegative(),
  dailyEnergyKilocalories: z.number().int().positive(),
  minDailyEnergyKilocalories: z.number().int().positive(),
  dailyCaloriesPercent: z.number().nonnegative(),
})

export type CatSubscriptionPreviewAnimal = z.infer<typeof CatSubscriptionPreviewAnimalSchema>

const CatSubscriptionPreviewSchema = z.object({
  planType: z.nativeEnum(FeedingPlanType),
  cadence: z.number().int().positive(),
  totalPrice: z.number().int().nonnegative(),
  dailyPrice: z.number().int().nonnegative(),
  totalSkus: z.number().int().nonnegative(),
  status: z.nativeEnum(SubscriptionStatus),
  animals: CatSubscriptionPreviewAnimalSchema.array(),
  skus: z
    .object({
      productVariantId: z.string().uuid(),
      quantity: z.number().int().nonnegative(),
      price: z.number().int().nonnegative(),
    })
    .array(),
})

export type CatSubscriptionPreview = z.infer<typeof CatSubscriptionPreviewSchema>

export const CatMealRecommendationSchema = z.object({
  productCode: z.nativeEnum(TygaMeals),
  allergens: z.string().array(),
  illnesses: z.string().array(),
})

export type CatMealRecommendation = z.infer<typeof CatMealRecommendationSchema>

export const UpdateCatSubscriptionPlanPreviewOutputSchema = z.object({
  current: CatSubscriptionPreviewSchema,
  updated: CatSubscriptionPreviewSchema,
  nextDeliveryDate: z.string().datetime().nullable(),
  notRecommendedMeals: CatMealRecommendationSchema.array(),
})

export type UpdateCatSubscriptionPlanPreviewOutput = z.infer<typeof UpdateCatSubscriptionPlanPreviewOutputSchema>

export const UpdateCatSubscriptionPlanOutputSchema = CatSubscriptionPreviewSchema.extend({
  nextDeliveryDate: z.string().datetime().nullable(),
})

export type UpdateCatSubscriptionPlanOutput = z.infer<typeof UpdateCatSubscriptionPlanOutputSchema>

export const ReactivateCatFromSubscriptionInputSchema = z.object({
  catId: z.string().uuid(),
  userId: z.string().uuid(),
  subscriptionId: z.string().uuid(),
  meals: z.nativeEnum(TygaMeals).array().optional(),
  skusPerDay: z.number().nonnegative().optional(),
  workflowId: z.string().optional(),
})

export type ReactivateCatFromSubscriptionInput = z.infer<typeof ReactivateCatFromSubscriptionInputSchema>

export const AddCatToSubscriptionInputSchema = ReactivateCatFromSubscriptionInputSchema.extend(
  CreateCatBodySchema.shape,
).omit({
  catId: true,
})

export type AddCatToSubscriptionInput = z.infer<typeof AddCatToSubscriptionInputSchema>

export const AddCatToSubscriptionPreviewOutputSchema = UpdateCatSubscriptionPlanPreviewOutputSchema.omit({
  notRecommendedMeals: true,
}).extend({
  notRecommendedMeals: z.record(z.string(), CatMealRecommendationSchema.array()),
})

export type AddCatToSubscriptionPreviewOutput = z.infer<typeof AddCatToSubscriptionPreviewOutputSchema>

export const ReactivateCatFromSubscriptionPreviewOutputSchema = AddCatToSubscriptionPreviewOutputSchema

export type ReactivateCatFromSubscriptionPreviewOutput = z.infer<
  typeof ReactivateCatFromSubscriptionPreviewOutputSchema
>

export const RemoveCatFromSubscriptionInputSchema = z.object({
  workflowId: z.string().optional(),
  animalId: z.string().uuid(),
  deactivateReasons: z.object({
    primaryReasonId: z.string().uuid(),
    secondaryReasonId: z.string().uuid().optional(),
    otherReason: z.string().optional(),
  }),
  subscriptionId: z.string().uuid(),
  subscriptionCancelReasons: z
    .object({
      primaryCancelReasonId: z.string().uuid(),
      secondaryCancelReasonId: z.string().uuid().optional(),
      otherCancelReason: z.string().optional(),
      cancellingUserId: z.string().uuid(),
      cancelledByAdmin: z.boolean(),
    })
    .optional(),
})

export type RemoveCatFromSubscriptionInput = z.infer<typeof RemoveCatFromSubscriptionInputSchema>

export const RemoveCatFromSubscriptionPreviewOutputSchema = z.object({
  current: CatSubscriptionPreviewSchema,
  updated: CatSubscriptionPreviewSchema,
  nextDeliveryDate: z.string().datetime().nullable(),
  notRecommendedMeals: z
    .object({
      productCode: z.nativeEnum(TygaMeals),
      allergens: z.string().array(),
      illnesses: z.string().array(),
    })
    .array(),
})

export type RemoveCatFromSubscriptionPreviewOutput = z.infer<typeof RemoveCatFromSubscriptionPreviewOutputSchema>

export const RemoveCatFromSubscriptionOutputSchema = CatSubscriptionPreviewSchema.extend({
  nextDeliveryDate: z.string().datetime().nullable(),
})

export type RemoveCatFromSubscriptionOutput = z.infer<typeof RemoveCatFromSubscriptionOutputSchema>

export const UpdateSubscriptionExtrasInputSchema = z.object({
  subscriptionId: z.string().uuid(),
  brand: z.union([z.literal(Brand.LYKA), z.literal(Brand.TYGA)]),
  extrasToAdd: z
    .object({
      productVariantId: z.string().uuid(),
      startOrderId: z.string().uuid(),
      nthOrder: z.number().int().gte(1),
      quantity: z.number().int().gte(1),
    })
    .array(),
  extrasToUpdate: z
    .object({
      id: z.string().uuid(),
      startOrderId: z.string().uuid(),
      nthOrder: z.number().int().gte(1),
      quantity: z.number().int().gte(1),
    })
    .array(),
  applyToPaidOrder: z.boolean().optional(),
})
export type UpdateSubscriptionExtrasInput = z.infer<typeof UpdateSubscriptionExtrasInputSchema>

export const UpdateSubscriptionExtrasPreviewOutputSchema = z.object({
  current: z.object({
    dogs: PreviewDogSchema.array(),
    cats: PreviewCatSchema.array(),
    subscription: PreviewSubscriptionSchema.extend({
      feedingPlanType: z.union([
        z.literal('full_bowl'),
        z.literal('half_bowl'),
        z.literal('tyga_full_bowl'),
        z.literal('tyga_mixed_bowl'),
      ]),
    }),
    subscriptionExtras: PreviewSubscriptionExtraSchema.array(),
  }),
  toUpdate: z.object({
    dogs: PreviewDogSchema.array(),
    cats: PreviewCatSchema.array(),
    subscription: PreviewSubscriptionSchema.extend({
      feedingPlanType: z.union([
        z.literal('full_bowl'),
        z.literal('half_bowl'),
        z.literal('tyga_full_bowl'),
        z.literal('tyga_mixed_bowl'),
      ]),
    }),
    subscriptionExtras: PreviewSubscriptionExtraSchema.array(),
  }),
  paidOrderAdjustment: PaidOrderAdjustmentSchema.optional(),
})
export type UpdateSubscriptionExtrasPreviewOutput = z.infer<typeof UpdateSubscriptionExtrasPreviewOutputSchema>

export const UpdateSubscriptionExtrasOutputSchema = z.object({
  subscription: SubscriptionSchema,
  updatedOrderIds: z.string().uuid().array(),
})
export type UpdateSubscriptionExtrasOutput = z.infer<typeof UpdateSubscriptionExtrasOutputSchema>

export const RevertMigrateUserEventSchema = z.object({
  email: z.string().email(),
  startDate: z.string().date().describe('Date in YYYY-MM-DD format'),
  endDate: z.string().date().optional().describe('Date in YYYY-MM-DD format, if not provided will take today date'),
})

export type RevertMigrateUserEvent = z.infer<typeof RevertMigrateUserEventSchema>

// Phoenix toggle reconciliation
export const ReconcilePhoenixToggleInputSchema = z.object({
  makeDateStart: z
    .string()
    .date()
    .optional()
    .describe('Start of make_date range (YYYY-MM-DD). Defaults to today AEST.'),
  makeDateEnd: z.string().date().optional().describe('End of make_date range (YYYY-MM-DD). Defaults to today AEST.'),
  facilityIds: z
    .array(z.number().int().positive())
    .optional()
    .describe('Facility IDs to reconcile. Defaults to [1, 2].'),
  isScheduledRun: z.boolean().optional().default(false),
  isDailyReport: z.boolean().optional().default(true),
})

export type ReconcilePhoenixToggleInput = z.infer<typeof ReconcilePhoenixToggleInputSchema>

export const ReconcilePhoenixToggleOutputSchema = z.object({
  makeDateRange: z.string(),
  facilityResults: z.array(
    z.object({
      facilityId: z.number(),
      facilityName: z.string(),
      facilityShortName: z.string(),
      hasDiscrepancies: z.boolean(),
      success: z.boolean(),
      error: z.string().optional(),
    }),
  ),
  hasAnyDiscrepancies: z.boolean(),
  slackMessageSent: z.boolean(),
})

export type ReconcilePhoenixToggleOutput = z.infer<typeof ReconcilePhoenixToggleOutputSchema>

// Daily NetSuite sync digest
export const NetsuiteSyncSummaryInputSchema = z.object({
  date: z
    .string()
    .date()
    .optional()
    .describe('Calendar day to report on (YYYY-MM-DD). Defaults to today in `timezone`.'),
  timezone: z.string().optional().default('Australia/Melbourne').describe('Timezone the calendar day is measured in.'),
  isScheduledRun: z.boolean().optional().default(false),
  isDailyReport: z
    .boolean()
    .optional()
    .default(true)
    .describe('Full report (always posts to Slack) vs alert-only check (posts only when something needs attention).'),
})

export type NetsuiteSyncSummaryInput = z.infer<typeof NetsuiteSyncSummaryInputSchema>

export const NetsuiteSyncSummaryOutputSchema = z.object({
  date: z.string(),
  timezone: z.string(),
  needsAttentionTotal: z.number().int().nonnegative(),
  slackMessageSent: z.boolean(),
})

export type NetsuiteSyncSummaryOutput = z.infer<typeof NetsuiteSyncSummaryOutputSchema>

export const BackfillOrderLineItemProductTypeInputSchema = z.object({
  batchSize: z.number().int().positive().optional().default(10_000).describe('Number of rows to update per batch.'),
  totalRowsAffected: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Running total passed across continueAsNew cycles.'),
})
export type BackfillOrderLineItemProductTypeInput = z.infer<typeof BackfillOrderLineItemProductTypeInputSchema>

export const BackfillOrderLineItemProductTypeOutputSchema = z.object({
  totalRowsAffected: z.number().int().nonnegative(),
  done: z.boolean(),
})
export type BackfillOrderLineItemProductTypeOutput = z.infer<typeof BackfillOrderLineItemProductTypeOutputSchema>

// ─── Backfill Order Animal Servings (daily_serving_size_in_grams) ────────────────
export const BackfillOrderAnimalServingsInputSchema = z.object({
  dryRun: z
    .boolean()
    .optional()
    .default(false)
    .describe('Derive + compare every order but do not persist any changes. Use with userId to verify data.'),
  concurrency: z
    .number()
    .int()
    .positive()
    .max(100)
    .optional()
    .describe('Max child (per-user) workflows in flight. Defaults to 25.'),
  userId: z
    .string()
    .uuid()
    .optional()
    .describe("Optional: run only this customer's Lyka orders instead of paging the whole cohort."),
  cursor: z
    .number()
    .int()
    .positive()
    .optional()
    .describe('Optional: start paging from this orderNumber (exclusive) instead of the beginning of the cohort.'),
})
export type BackfillOrderAnimalServingsInput = z.infer<typeof BackfillOrderAnimalServingsInputSchema>

export const BackfillOrderAnimalServingsOutputSchema = z.object({
  cursor: z
    .number()
    .int()
    .nullable()
    .describe('Last orderNumber processed; null once the cohort is exhausted (always null in single-user mode).'),
  ordersSeen: z.number().int().nonnegative(),
  ordersUpdated: z
    .number()
    .int()
    .nonnegative()
    .describe('Orders whose animal servings were written (or, in dryRun, would have changed).'),
  ordersUnchanged: z.number().int().nonnegative().describe('Orders whose servings already matched what is stored.'),
  ordersSkipped: z.number().int().nonnegative().describe('Non-Lyka orders or orders with no meal line items.'),
  ordersFlagged: z
    .number()
    .int()
    .nonnegative()
    .describe(
      'Orders with an animal resolvable in neither the snapshot nor the latest subscription — logged, not written.',
    ),
  ordersFailed: z.number().int().nonnegative(),
  usersProcessed: z.number().int().nonnegative(),
  done: z.boolean(),
})
export type BackfillOrderAnimalServingsOutput = z.infer<typeof BackfillOrderAnimalServingsOutputSchema>

// ─── Backfill Order Pricing (unitPrice / charge fields) ─────────────────────────
export const BackfillOrderPricingInputSchema = z.object({
  cohort: z
    .nativeEnum(OrderPricingBackfillCohort)
    .describe(
      'tyga = all Tyga orders; lyka = Phoenix-generated Lyka orders (phoenix_toggle on + no order_id_mapping / legacyId)',
    ),
  dryRun: z.boolean().optional().default(false).describe('Reprice every order but do not persist any changes.'),
  concurrency: z
    .number()
    .int()
    .positive()
    .max(100)
    .optional()
    .describe('Max child (per-user) workflows in flight. Defaults to 25.'),
  paymentStatus: z
    .nativeEnum(PaymentStatus)
    .optional()
    .describe('Optional payment_status filter; omit to cover all statuses (past + pending).'),
})
export type BackfillOrderPricingInput = z.infer<typeof BackfillOrderPricingInputSchema>

export const BackfillOrderPricingOutputSchema = z.object({
  cursor: z.number().int().nullable().describe('Last orderNumber processed; null once the cohort is exhausted.'),
  ordersSeen: z.number().int().nonnegative(),
  ordersRepriced: z.number().int().nonnegative(),
  ordersSkipped: z.number().int().nonnegative().describe('Orders skipped (no subscription / order index).'),
  ordersFailed: z.number().int().nonnegative(),
  usersProcessed: z.number().int().nonnegative(),
  done: z.boolean(),
})
export type BackfillOrderPricingOutput = z.infer<typeof BackfillOrderPricingOutputSchema>

// ─── Backfill Order subscription_history_id ─────────────────────────────────────
export const BackfillOrderSubscriptionHistoryInputSchema = z.object({
  cohort: z
    .nativeEnum(OrderPricingBackfillCohort)
    .describe(
      'tyga = all Tyga orders; lyka = Phoenix-generated Lyka orders (phoenix_toggle on + no order_id_mapping / legacyId)',
    ),
  dryRun: z.boolean().optional().default(false).describe('Resolve every order but do not persist any changes.'),
  concurrency: z
    .number()
    .int()
    .positive()
    .max(100)
    .optional()
    .describe('Max child (per-subscription) workflows in flight. Defaults to 25.'),
  paymentStatus: z
    .nativeEnum(PaymentStatus)
    .optional()
    .describe(
      'Optional payment_status filter; omit to cover all statuses. Pending orders are normally handled by a DB migration.',
    ),
})
export type BackfillOrderSubscriptionHistoryInput = z.infer<typeof BackfillOrderSubscriptionHistoryInputSchema>

export const BackfillOrderSubscriptionHistoryOutputSchema = z.object({
  cursor: z.number().int().nullable().describe('Last orderNumber processed; null once the cohort is exhausted.'),
  ordersSeen: z.number().int().nonnegative(),
  ordersMatchedHistory: z.number().int().nonnegative().describe('Orders set to a subscriptions_history snapshot id.'),
  ordersFellBackToSubscription: z
    .number()
    .int()
    .nonnegative()
    .describe('Orders with no earlier snapshot; set to subscriptionId.'),
  ordersFailed: z.number().int().nonnegative(),
  subscriptionsProcessed: z.number().int().nonnegative(),
  done: z.boolean(),
})
export type BackfillOrderSubscriptionHistoryOutput = z.infer<typeof BackfillOrderSubscriptionHistoryOutputSchema>

// ─── Consolidate migrated Lyka orders onto the active subscription ──────────────
export const ConsolidateMigratedOrderSubscriptionsInputSchema = z.object({
  userId: z
    .string()
    .uuid()
    .optional()
    .describe('Restrict the run to a single user (canary/testing); omit to cover all migrated phoenix-toggle users.'),
  dryRun: z
    .boolean()
    .optional()
    .default(false)
    .describe("Resolve every user's live subscription but do not move/reindex any orders."),
  concurrency: z
    .number()
    .int()
    .positive()
    .max(100)
    .optional()
    .describe('Max child (per-user) workflows in flight. Defaults to 25.'),
})
export type ConsolidateMigratedOrderSubscriptionsInput = z.infer<
  typeof ConsolidateMigratedOrderSubscriptionsInputSchema
>

export const ConsolidateMigratedOrderSubscriptionsOutputSchema = z.object({
  cursor: z.number().int().nullable().describe('Last orderNumber processed; null once the cohort is exhausted.'),
  ordersSeen: z.number().int().nonnegative(),
  usersProcessed: z.number().int().nonnegative(),
  ordersMoved: z.number().int().nonnegative().describe('Migrated orders ported + re-pointed to the live subscription.'),
  ordersReindexed: z.number().int().nonnegative().describe('Total orders re-sequenced under live subscriptions.'),
  usersSkippedNoSubscription: z
    .number()
    .int()
    .nonnegative()
    .describe('Users with no live subscription to consolidate onto.'),
  usersFailed: z.number().int().nonnegative(),
  done: z.boolean(),
})
export type ConsolidateMigratedOrderSubscriptionsOutput = z.infer<
  typeof ConsolidateMigratedOrderSubscriptionsOutputSchema
>

// --- Consolidate ALL phoenix-toggle orders of a brand onto the active subscription ---
export const ConsolidatePhoenixOrderSubscriptionsInputSchema = z.object({
  userId: z
    .string()
    .uuid()
    .optional()
    .describe('Restrict the run to a single user (canary/testing); omit to cover all phoenix-toggle customers.'),
  dryRun: z
    .boolean()
    .optional()
    .default(false)
    .describe("Resolve every user's active subscription but do not move/reindex any orders."),
  concurrency: z
    .number()
    .int()
    .positive()
    .max(100)
    .optional()
    .describe('Max child (per-user) workflows in flight. Defaults to 25.'),
  // Optional, not defaulted: omitting must keep working for every pre-brand caller and history.
  brand: z.nativeEnum(Brand).optional()
    .describe("Which brand's phoenix-toggle orders to consolidate onto that brand's active subscription. Omit for Lyka (the default); pass 'tyga' for a Tyga sweep - child workflow ids are brand-scoped so both can run without colliding."),
})
export type ConsolidatePhoenixOrderSubscriptionsInput = z.infer<typeof ConsolidatePhoenixOrderSubscriptionsInputSchema>

export const ConsolidatePhoenixOrderSubscriptionsOutputSchema = z.object({
  cursor: z
    .string()
    .uuid()
    .nullable()
    .describe('Last customer id processed; null once the cohort is exhausted (always null in single-user mode).'),
  usersSeen: z.number().int().nonnegative(),
  usersProcessed: z.number().int().nonnegative(),
  ordersMoved: z.number().int().nonnegative().describe('Lyka orders re-pointed to the active subscription.'),
  ordersReindexed: z.number().int().nonnegative().describe('Total orders re-sequenced under active subscriptions.'),
  usersSkippedNoSubscription: z
    .number()
    .int()
    .nonnegative()
    .describe('Users with no active Lyka subscription to consolidate onto.'),
  usersFailed: z.number().int().nonnegative(),
  done: z.boolean(),
})
export type ConsolidatePhoenixOrderSubscriptionsOutput = z.infer<
  typeof ConsolidatePhoenixOrderSubscriptionsOutputSchema
>

// ─── Backfill Order Cutoffs (order_cutoffs cache) ───────────────────────────────
export const BackfillOrderCutoffsInputSchema = z.object({
  deliveryDateFrom: z
    .string()
    .datetime()
    .optional()
    .describe('UTC lower bound on delivery_date; defaults to start of today UTC'),
  concurrency: z
    .number()
    .int()
    .positive()
    .max(20)
    .optional()
    .default(5)
    .describe('Max concurrent resolve calls per page. Defaults to 5.'),
  pageSize: z
    .number()
    .int()
    .positive()
    .max(500)
    .optional()
    .default(200)
    .describe('Orders fetched per page. Defaults to 200.'),
  includeNonProcessed: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      'When true, also re-resolve non-processed orders that already have an order_cutoffs row. Processed orders with existing rows are always excluded.',
    ),
})
export type BackfillOrderCutoffsInput = z.input<typeof BackfillOrderCutoffsInputSchema>

export const BackfillOrderCutoffsOutputSchema = z.object({
  cursor: z
    .object({
      deliveryDate: z.string().datetime(),
      orderId: z.string().uuid(),
    })
    .nullable()
    .describe('Last (deliveryDate, orderId) processed; null once exhausted.'),
  ordersAttempted: z.number().int().nonnegative(),
  ordersFailed: z
    .number()
    .int()
    .nonnegative()
    .describe('Resolve HTTP failures only; resolve itself is best-effort per order.'),
  done: z.boolean(),
})
export type BackfillOrderCutoffsOutput = z.infer<typeof BackfillOrderCutoffsOutputSchema>

// ─── Backfill Meal Plan Snapshots (OPS-3222) ────────────────────────────────
// Pages paid, Phoenix, not-yet-started orders and rebuilds the meal plan snapshot on
// their fulfilments by reusing the (idempotent) per-order refresh activity. Repairs both
// missing snapshots (the phoenix_toggle race) and snapshots built with empty recipes.
export const BackfillMealPlanSnapshotsInputSchema = z.object({
  concurrency: z
    .number()
    .int()
    .positive()
    .max(20)
    .optional()
    .default(5)
    .describe('Max concurrent per-order refresh activities per page. Defaults to 5.'),
  pageSize: z
    .number()
    .int()
    .positive()
    .max(500)
    .optional()
    .default(200)
    .describe('Orders fetched per page. Defaults to 200.'),
})
export type BackfillMealPlanSnapshotsInput = z.input<typeof BackfillMealPlanSnapshotsInputSchema>

export const BackfillMealPlanSnapshotsOutcomeCountsSchema = z.object({
  refreshed: z.number().int().nonnegative(),
  no_active_fulfilments: z.number().int().nonnegative(),
  fulfilment_started: z.number().int().nonnegative(),
  snapshot_unavailable: z.number().int().nonnegative(),
  write_failed: z.number().int().nonnegative(),
  error: z.number().int().nonnegative(),
})
export type BackfillMealPlanSnapshotsOutcomeCounts = z.infer<typeof BackfillMealPlanSnapshotsOutcomeCountsSchema>

export const BackfillMealPlanSnapshotsOutputSchema = z.object({
  cursor: z
    .object({
      orderId: z.string().uuid(),
    })
    .nullable()
    .describe('Last order id processed; null once the sweep is exhausted.'),
  ordersAttempted: z.number().int().nonnegative(),
  outcomeCounts: BackfillMealPlanSnapshotsOutcomeCountsSchema.describe(
    'Per-order refresh outcomes accumulated across the whole sweep.',
  ),
  done: z.boolean(),
})
export type BackfillMealPlanSnapshotsOutput = z.infer<typeof BackfillMealPlanSnapshotsOutputSchema>

// ─── Backfill Fulfilment Customer IDs (OPS-3212) ─────────────────────────────
export { CustomerIdBackfillCursorSchema } from '../fulfilment/schemas'
export type { CustomerIdBackfillCursor } from '../fulfilment/schemas'

export const BackfillFulfilmentCustomerIdsInputSchema = z.object({
  concurrency: z
    .number()
    .int()
    .positive()
    .max(20)
    .optional()
    .default(5)
    .describe('Max concurrent per-fulfilment backfill activities per page. Defaults to 5.'),
  pageSize: z
    .number()
    .int()
    .positive()
    .max(500)
    .optional()
    .default(200)
    .describe('Fulfilments fetched per page. Defaults to 200.'),
  resumeCursor: CustomerIdBackfillCursorSchema.optional().describe(
    'Optional: start paging after this fulfilment id (exclusive). Use the cursor from a failed or interrupted run\'s Temporal progress query, or from a prior run that stopped before done.',
  ),
})
export type BackfillFulfilmentCustomerIdsInput = z.input<typeof BackfillFulfilmentCustomerIdsInputSchema>

export const BackfillFulfilmentCustomerIdsOutcomeCountsSchema = z.object({
  updated: z.number().int().nonnegative(),
  already_correct: z.number().int().nonnegative(),
  order_not_found: z.number().int().nonnegative(),
  write_failed: z.number().int().nonnegative(),
  error: z.number().int().nonnegative(),
})
export type BackfillFulfilmentCustomerIdsOutcomeCounts = z.infer<typeof BackfillFulfilmentCustomerIdsOutcomeCountsSchema>

export const BackfillFulfilmentCustomerIdsOutputSchema = z.object({
  cursor: CustomerIdBackfillCursorSchema.nullable().describe(
    'Last fulfilment id from the final completed page; null once the sweep is exhausted. Pass as resumeCursor on a follow-up POST to continue where a failed run left off.',
  ),
  fulfilmentsAttempted: z.number().int().nonnegative(),
  outcomeCounts: BackfillFulfilmentCustomerIdsOutcomeCountsSchema.describe(
    'Per-fulfilment backfill outcomes accumulated across the whole sweep.',
  ),
  done: z.boolean(),
})
export type BackfillFulfilmentCustomerIdsOutput = z.infer<typeof BackfillFulfilmentCustomerIdsOutputSchema>

// ─── Reschedule Blocked Orders ──────────────────────────────────────────────

export const RescheduleBlockedOrdersInputSchema = z
  .object({
    blockedDates: z
      .array(z.string().date())
      .min(1)
      .optional()
      .describe('Dates (YYYY-MM-DD) to reschedule orders for — fetches all operational blocks on each date'),
    operationalBlockId: z
      .number()
      .positive()
      .optional()
      .describe('ID of a single operational block — derives date and scope from the block record'),
    brands: z
      .array(z.nativeEnum(Brand))
      .min(1)
      .optional()
      .describe('When set, process only these brands from the block (e.g. ["lyka"] to skip Tyga until ready)'),
    orderIds: z
      .array(z.string().uuid())
      .min(1)
      .optional()
      .describe('Process only these specific orders (still validated against block scope and dates)'),
    userIds: z.array(z.string().uuid()).min(1).optional().describe('Process only orders belonging to these users'),
    dryRun: z.boolean().optional().default(false).describe('When true, returns affected orders without making changes'),
    batchSize: z
      .number()
      .int()
      .positive()
      .optional()
      .default(25)
      .describe('Number of orders to process in parallel per batch'),
  })
  .superRefine((data, ctx) => {
    const hasBlockedDates = data.blockedDates != null && data.blockedDates.length > 0
    const hasOperationalBlockId = data.operationalBlockId != null

    if (hasBlockedDates === hasOperationalBlockId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Provide exactly one of blockedDates or operationalBlockId',
        path: ['blockedDates'],
      })
    }
  })
export type RescheduleBlockedOrdersInput = z.input<typeof RescheduleBlockedOrdersInputSchema>

export const RescheduleResultStatusSchema = z.enum(['rescheduled', 'skipped', 'flagged', 'failed', 'dry_run'])

export const RescheduleResultSchema = z.object({
  orderId: z.string(),
  status: RescheduleResultStatusSchema,
  reason: z.string().optional(),
  oldDeliveryDate: z.string().optional(),
  newDeliveryDate: z.string().optional(),
  oldMakeDate: z.string().optional(),
  newMakeDate: z.string().optional(),
  searchStrategy: z.enum(['extended', 'conservative', 'skip']).optional(),
})
export type RescheduleResult = z.infer<typeof RescheduleResultSchema>

export const BlockSummarySchema = z.object({
  blockId: z.string(),
  blockDate: z.string(),
  blockType: z.string(),
  totalOrders: z.number().int(),
  rescheduled: z.number().int().describe('Orders actually patched (status=rescheduled only)'),
  wouldReschedule: z.number().int().describe('Orders that would move (status=rescheduled or dry_run)'),
  skipped: z.number().int(),
  flagged: z.number().int(),
  failed: z.number().int(),
  results: z.array(RescheduleResultSchema),
})
export type BlockSummary = z.infer<typeof BlockSummarySchema>

export const RescheduleBlockedOrdersOutputSchema = z.object({
  blockedDates: z.array(z.string()),
  dryRun: z.boolean(),
  totalOrders: z.number().int(),
  rescheduled: z.number().int().describe('Orders actually patched (status=rescheduled only)'),
  wouldReschedule: z.number().int().describe('Orders that would move (status=rescheduled or dry_run)'),
  skipped: z.number().int(),
  flagged: z.number().int(),
  failed: z.number().int(),
  blocks: z.array(BlockSummarySchema),
})

export const DuplicateOrderInputSchema = z.object({
  userId: z.string().uuid().describe('Customer the new order is for'),
  orderId: z.string().uuid().describe('Source order to clone'),
  deliveryDate: z.string().describe('Delivery date for the duplicate (ISO date, YYYY-MM-DD)'),
  deliveryWindow: z.nativeEnum(DeliverySlotWindow).describe('AM or PM delivery slot'),
  duplicateReason: z.string().min(1).describe('Reason logged on the source order and sent to Slack'),
  shiftFutureOrders: z
    .boolean()
    .describe("If true, push the subscription's upcoming orders forward by cadence to keep rhythm"),
  createdBy: z.string().uuid().describe('The admin/CC actor triggering the duplicate, for audit'),
})
export type DuplicateOrderInput = z.infer<typeof DuplicateOrderInputSchema>

export const DuplicateOrderOutputSchema = z.object({
  orderId: z.string().uuid().describe('The newly created duplicate order id'),
  sourceOrderId: z.string().uuid(),
  futureOrdersShifted: z.boolean(),
})
export type DuplicateOrderOutput = z.infer<typeof DuplicateOrderOutputSchema>
export type RescheduleBlockedOrdersOutput = z.infer<typeof RescheduleBlockedOrdersOutputSchema>

// ─── RAF Coupon Migration: Campaign Map ──────────────────────────────────────
// Used by the RAF coupon migration activities (getRafCampaigns) and workflows
// (resolveRafCampaignId). The map is an array (not a hash) because coupon →
// campaign resolution is a date range lookup, not a key lookup.
export const CampaignMapEntrySchema = z.object({
  phoenixId: z.string().uuid(),
  endDate: z.string(),
  name: z.string(),
  startDate: z.string().nullable().optional(),
})
export type CampaignMapEntry = z.infer<typeof CampaignMapEntrySchema>

// ─── RAF campaign launch ────────────────────────────────────────────────────

export const LaunchRafCampaignTierInputSchema = z.object({
  /**
   * Which tier this is, counting from 1. Tiers must run 1, 2, 3… with no gaps: allocation walks
   * them in this order and the account page draws them as a row of numbered steps.
   */
  referralCountRequired: z.number().int().positive(),
  /**
   * The referrer's *lifetime* referral count beyond which this tier can no longer be given, so a
   * perk can be aimed at newer referrers only. Omit, or send null, for no limit.
   *
   * Setting it on any tier switches the whole campaign to handing out tiers in order rather than by
   * referral number. Once that is on, someone who has referred twice before gets this campaign's
   * first tier on their next referral, instead of jumping to its third.
   */
  maxReferralPosition: z.number().int().positive().nullable().optional(),
  /** Merch given at this tier, by SKU. Empty means the referrer discount and nothing physical. */
  skus: z.array(z.string().min(1)).default([]),
})
export type LaunchRafCampaignTierInput = z.infer<typeof LaunchRafCampaignTierInputSchema>

export const LaunchRafCampaignInputSchema = z.object({
  /** Identifies the campaign together with `brand`. Re-sending an existing name updates it. */
  name: z.string().min(1).max(60),
  brand: z.nativeEnum(Brand),
  description: z.string().max(800).optional(),
  /**
   * The day the campaign opens. Required, launching moves every existing coupon
   * onto this campaign, so the day it happens is not something to infer from a blank field.
   *
   * Must not be in the future, because those coupons have to point at whatever is live now, and
   * must be after the start date of the campaign it replaces.
   */
  startDate: z.string().date(),
  endDate: z.string().date(),
  /** Must already exist in pricing. Create it there first; this only points at it. */
  referrerDiscountId: z.string().uuid(),
  refereeDiscountId: z.string().uuid(),
  tiers: z.array(LaunchRafCampaignTierInputSchema).min(1).max(20),
  /**
   * Pricing deletes any stored tier this payload does not mention. Re-launching a live campaign
   * with a shortened tier list would take perks away from referrers partway through earning them,
   * so it is refused unless this is set deliberately.
   */
  allowTierRemoval: z.boolean().optional().default(false),
  /**
   * Runs every check and reports the whole plan without writing anything. This is the rehearsal:
   * it resolves the discounts and reads their real percentages back, names each SKU's product, and
   * counts the coupons that would move. Run it in each environment before the real thing.
   */
  dryRun: z.boolean().optional().default(false),
})
export type LaunchRafCampaignInput = z.infer<typeof LaunchRafCampaignInputSchema>

/** A discount as pricing actually holds it, so the money can be read back before committing. */
export const LaunchRafCampaignDiscountSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** One line per order position, e.g. "1st order: 50% off meal". */
  summary: z.array(z.string()),
})

/**
 * A SKU resolved against the product catalogue of whichever environment is running, carrying enough
 * naming to recognise it. Variants get their ids at insert time, so each environment has its own and
 * anything naming a product across environments has to travel as a SKU and be resolved on arrival.
 */
export const ResolvedSkuVariantSchema = z.object({
  sku: z.string(),
  productVariantId: z.string().uuid(),
  productName: z.string(),
  variantName: z.string().nullable(),
  productType: z.string(),
})
export type ResolvedSkuVariant = z.infer<typeof ResolvedSkuVariantSchema>

export const LaunchRafCampaignTierPlanSchema = z.object({
  referralCountRequired: z.number().int().positive(),
  maxReferralPosition: z.number().int().positive().nullable(),
  perks: z.array(ResolvedSkuVariantSchema),
  /** How this tier compares to what is already stored for the campaign. */
  change: z.enum(['added', 'unchanged', 'changed']),
})

export const LaunchRafCampaignOutputSchema = z.object({
  /** Null only on a dry run of a campaign that does not exist in this environment yet. */
  campaignId: z.string().uuid().nullable(),
  campaignName: z.string(),
  brand: z.nativeEnum(Brand),
  startDate: z.string(),
  endDate: z.string(),
  /** False when this updated a campaign that was already there, which a re-run always does. */
  isNewCampaign: z.boolean(),
  referrerDiscount: LaunchRafCampaignDiscountSchema,
  refereeDiscount: LaunchRafCampaignDiscountSchema,
  tiers: z.array(LaunchRafCampaignTierPlanSchema),
  /** Stored tiers this payload would delete. Non-empty only with `allowTierRemoval`. */
  removedTiers: z.array(z.number().int().positive()),
  /** The campaign that stopped covering the start date, or null when none was running. */
  closedCampaign: z
    .object({
      name: z.string(),
      previousEndDate: z.string(),
      endDate: z.string(),
    })
    .nullable(),
  /** Null on a dry run of a campaign that does not exist yet, so there is nothing to count against. */
  couponsEligible: z.number().int().nonnegative().nullable(),
  /** Zero on a dry run, otherwise the same as `couponsEligible`. */
  couponsMoved: z.number().int().nonnegative(),
  dryRun: z.boolean(),
})
export type LaunchRafCampaignOutput = z.infer<typeof LaunchRafCampaignOutputSchema>

// ─── RAF Bulk Migration ─────────────────────────────────────────────────────
/**
 * Phoenix UUID for the October 2025 Referee discount (50% first order, 30%
 * second order). Equivalent to `uuidFromInteger(77)` — the Laravel discount
 * ID is 77. This discount is created by the discounts team's Laravel→Phoenix
 * migration; the RAF migration assigns it uniformly to every promotion.
 */
export const DEFAULT_RAF_REFEREE_DISCOUNT_ID = '00000000-0000-0000-0000-000000000077'

export const BulkMigrateRafCouponsInputSchema = z.object({
  dryRun: z.boolean().optional().default(false),
  pageSize: z.number().int().min(1).max(1000).optional(),
  resumeCursor: z.number().int().nonnegative().optional(),
  /**
   * Phoenix discount UUID assigned to every migrated RAF promotion. Defaults
   * to the October 2025 Referee discount. The workflow verifies this discount
   * exists in Phoenix before processing any pages.
   */
  refereeDiscountId: z.string().uuid().optional().default(DEFAULT_RAF_REFEREE_DISCOUNT_ID),
  categoryId: z.string().uuid(),
  accumulatedStats: z
    .object({
      totalCreated: z.number().int().nonnegative(),
      totalUpdated: z.number().int().nonnegative(),
      totalSkipped: z.number().int().nonnegative(),
      totalFailed: z.number().int().nonnegative(),
      failedPages: z.array(z.number().int().nonnegative()),
      campaignMap: z.array(CampaignMapEntrySchema).optional(),
    })
    .optional(),
})
export type BulkMigrateRafCouponsInput = z.infer<typeof BulkMigrateRafCouponsInputSchema>

export const CouponFailureDetailSchema = z.object({
  code: z.string(),
  error: z.string(),
})

export const BulkMigrateRafCouponsOutputSchema = z.object({
  totalRecords: z.number().int().nonnegative(),
  totalCreated: z.number().int().nonnegative(),
  totalUpdated: z.number().int().nonnegative(),
  totalSkipped: z.number().int().nonnegative(),
  totalFailed: z.number().int().nonnegative(),
  failedPages: z.array(z.number().int().nonnegative()),
  /**
   * The last cursor that was successfully processed. Use as `resumeCursor`
   *  in a follow-up run to continue where this execution left off.
   */
  lastProcessedCursor: z.number().int().nonnegative(),
  /** Coupon codes that failed at pricing level — aggregated from all pages. */
  failedCouponCodes: z.array(CouponFailureDetailSchema),
  /** Laravel coupon IDs that were unmappable — aggregated from all pages. */
  skippedUnmappableCouponIds: z.array(z.number().int()),
})
export type BulkMigrateRafCouponsOutput = z.infer<typeof BulkMigrateRafCouponsOutputSchema>

export const MigrateRafCouponPageInputSchema = z.object({
  cursor: z.number().int().nonnegative(),
  pageSize: z.number().int().min(1).max(1000),
  refereeDiscountId: z.string().uuid(),
  categoryId: z.string().uuid(),
  campaignMap: z.array(CampaignMapEntrySchema).optional(),
  dryRun: z.boolean(),
})
export type MigrateRafCouponPageInput = z.infer<typeof MigrateRafCouponPageInputSchema>

export const MigrateRafCouponPageOutputSchema = z.object({
  nextCursor: z.number().int().nullable(),
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  /** Coupon codes that failed at the pricing service level (bulkCreate errors). */
  failedCouponCodes: z.array(CouponFailureDetailSchema),
  /** Laravel coupon IDs skipped because of null code. */
  skippedUnmappableCouponIds: z.array(z.number().int()),
})
export type MigrateRafCouponPageOutput = z.infer<typeof MigrateRafCouponPageOutputSchema>

// ─── RAF Coupon User Bulk Migration ─────────────────────────────────────────

export const BulkMigrateRafCouponUsersInputSchema = z.object({
  dryRun: z.boolean().optional().default(false),
  pageSize: z.number().int().min(1).max(1000).optional(),
  resumeCursor: z.number().int().nonnegative().optional(),
  accumulatedStats: z
    .object({
      totalCreated: z.number().int().nonnegative(),
      totalUpdated: z.number().int().nonnegative(),
      totalSkipped: z.number().int().nonnegative(),
      totalFailed: z.number().int().nonnegative(),
      failedPages: z.array(z.number().int().nonnegative()),
    })
    .optional(),
})
export type BulkMigrateRafCouponUsersInput = z.infer<typeof BulkMigrateRafCouponUsersInputSchema>

export const BulkMigrateRafCouponUsersOutputSchema = z.object({
  totalRecords: z.number().int().nonnegative(),
  totalCreated: z.number().int().nonnegative(),
  totalUpdated: z.number().int().nonnegative(),
  totalSkipped: z.number().int().nonnegative(),
  totalFailed: z.number().int().nonnegative(),
  failedPages: z.array(z.number().int().nonnegative()),
  lastProcessedCursor: z.number().int().nonnegative(),
})
export type BulkMigrateRafCouponUsersOutput = z.infer<typeof BulkMigrateRafCouponUsersOutputSchema>

export const RafCampaignDiscountMapEntrySchema = z.object({
  name: z.string(),
  refereeDiscountId: z.string().uuid().nullable(),
})
export type RafCampaignDiscountMapEntry = z.infer<typeof RafCampaignDiscountMapEntrySchema>

export const MigrateRafCouponUserPageInputSchema = z.object({
  cursor: z.number().int().nonnegative(),
  pageSize: z.number().int().min(1).max(1000),
  campaignDiscountMap: z.array(RafCampaignDiscountMapEntrySchema),
  dryRun: z.boolean(),
})
export type MigrateRafCouponUserPageInput = z.infer<typeof MigrateRafCouponUserPageInputSchema>

export const MigrateRafCouponUserPageOutputSchema = z.object({
  nextCursor: z.number().int().nullable(),
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
})
export type MigrateRafCouponUserPageOutput = z.infer<typeof MigrateRafCouponUserPageOutputSchema>

// ─── Seed Fulfilment Data ───────────────────────────────────────────────────

export const ServiceableState = z.enum(['NSW', 'VIC', 'QLD', 'SA', 'WA'])

/**
 * Each stage percentage is relative to the previous stage's output:
 *   created(1000) → paid 50%(500) → packed 100%(500) → palletised 50%(250) → delivered 100%(250)
 */
export const SeedFulfilmentDataInputSchema = z.object({
  userId: z.string().uuid().describe('Existing user ID to create orders for'),
  brand: z.nativeEnum(Brand).default(Brand.LYKA),
  numberOfRecords: z.number().int().positive().default(100).describe('Number of orders to create'),
  paidPercentage: z
    .number()
    .int()
    .min(0)
    .max(100)
    .default(100)
    .describe('Percentage of created orders to pay (PROCESSED) and send to fulfilment'),
  packedPercentage: z
    .number()
    .int()
    .min(0)
    .max(100)
    .default(0)
    .describe('Percentage of paid fulfilments to advance to PACKED'),
  palletisedPercentage: z
    .number()
    .int()
    .min(0)
    .max(100)
    .default(0)
    .describe('Percentage of packed fulfilments to advance to PALLETISED'),
  deliveredPercentage: z
    .number()
    .int()
    .min(0)
    .max(100)
    .default(0)
    .describe('Percentage of palletised fulfilments to mark as DELIVERED on the order'),
  states: ServiceableState.array()
    .optional()
    .describe('States to distribute records across. Defaults to all serviceable states.'),
  batchSize: z
    .number()
    .int()
    .positive()
    .max(50)
    .default(10)
    .describe('Concurrent operations per batch. Lower = less pressure on downstream services. Max 50.'),
  makeDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .describe('Override make date (YYYY-MM-DD). Defaults to 3 days from now.'),
  deliveryDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .describe('Override delivery date (YYYY-MM-DD). Defaults to 5 days from now.'),
})

export type SeedFulfilmentDataInput = z.infer<typeof SeedFulfilmentDataInputSchema>

export const SeedFulfilmentDataOutputSchema = z.object({
  totalOrders: z.number().int(),
  paidOrders: z.number().int(),
  failedPayments: z.number().int(),
  failedPaymentOrderIds: z.string().uuid().array(),
  failedFulfilments: z.number().int(),
  failedFulfilmentOrderIds: z.string().uuid().array(),
  totalFulfilments: z.number().int(),
  packedFulfilments: z.number().int(),
  failedPacking: z.number().int(),
  palletisedFulfilments: z.number().int(),
  failedPalletising: z.number().int(),
  deliveredFulfilments: z.number().int(),
  failedDelivering: z.number().int(),
  orderIds: z.string().uuid().array(),
})

export type SeedFulfilmentDataOutput = z.infer<typeof SeedFulfilmentDataOutputSchema>

export const CheckCouponExistsInputSchema = z.object({
  workflowId: z.string().optional(),
  brand: z.nativeEnum(Brand),
  value: z.string(),
})

export type CheckCouponExistsInput = z.infer<typeof CheckCouponExistsInputSchema>

export const CheckCouponExistsOutputSchema = z.object({
  exists: z.boolean(),
})

export type CheckCouponExistsOutput = z.infer<typeof CheckCouponExistsOutputSchema>

export const UpdateRafCouponInputSchema = z.object({
  workflowId: z.string().optional(),
  userId: z.string().uuid(),
  brand: z.nativeEnum(Brand),
  value: z.string(),
})

export type UpdateRafCouponInput = z.infer<typeof UpdateRafCouponInputSchema>

export const UpdateRafCouponOutputSchema = z.object({
  success: z.boolean(),
  userId: z.string().uuid(),
  brand: z.nativeEnum(Brand),
  value: z.string(),
})

export type UpdateRafCouponOutput = z.infer<typeof UpdateRafCouponOutputSchema>

export const UpsertNetsuiteCustomerInputSchema = z.object({
  phoenixCustomerId: z.string().uuid().describe('Phoenix customer UUID — also used as NetSuite externalId.'),
})

export type UpsertNetsuiteCustomerInput = z.infer<typeof UpsertNetsuiteCustomerInputSchema>

export const UpsertNetsuiteCustomerOutputSchema = z.object({
  netsuiteCustomerId: z.string(),
  source: z.enum(['flags', 'externalIdLookup', 'created']),
})

export type UpsertNetsuiteCustomerOutput = z.infer<typeof UpsertNetsuiteCustomerOutputSchema>

// ─── Enriched User Events ───────────────────────────────────────────────────

export const GetEnrichedUserEventsInputSchema = z
  .object({
    userId: z.string().uuid(),
    workflowId: z.string().optional(),
  })
  .extend(GetUserEventQuerySchema.shape)

export type GetEnrichedUserEventsInput = z.infer<typeof GetEnrichedUserEventsInputSchema>

export const GetEnrichedUserEventsOutputSchema = generateRequiredPaginationResult(
  z.object({
    ...GetUserEventResponseSchema.shape.data.element.shape,
    actorName: z.string().nullable(),
    userName: z.string().nullable(),
    animalName: z.string().nullable(),
  }),
)

export type GetEnrichedUserEventsOutput = z.infer<typeof GetEnrichedUserEventsOutputSchema>

export const UpdateSubscriptionPriceVersionInput = z.object({
  userId: z
    .string()
    .uuid()
    .optional()
    .describe('Run for a specific user id, will run/check against all user ids if none specified'),
  subscriptionStatus: z
    .nativeEnum(SubscriptionStatus)
    .optional()
    .describe('Update price version for cohort, if not specified will do all status'),
  dryRun: z.boolean().optional().describe('Simulate affected customers by a price increase'),
  migratedDate: z.string().datetime({ offset: true }).optional().describe('Date to apply increase for customers after'),
})

export type UpdateSubscriptionPriceVersionInputSchema = z.infer<typeof UpdateSubscriptionPriceVersionInput>

export const UpdateSubscriptionPriceVersionOutput = z.object({
  runId: z.string(),
  totals: z.object({
    customers: z.number().int(),
    active: z.number().int(),
    paused: z.number().int(),
    cancelled: z.number().int(),
    pendingOrdersAdjusted: z.number().int(),
  }),
})

export type UpdateSubscriptionPriceVersionOutputSchema = z.infer<typeof UpdateSubscriptionPriceVersionOutput>

export const RebalanceOrderMoveFailureSchema = z.object({
  orderId: z.string().uuid(),
  reason: z.string(),
})
export type RebalanceOrderMoveFailure = z.infer<typeof RebalanceOrderMoveFailureSchema>

export const MoveRebalanceOrdersInputSchema = z.object({
  workflowId: z.string().optional(),
  facilityId: z.number().int(),
  orderIds: z.array(z.string().uuid()).min(1),
  newMakeDate: z.string().date(),
  timezone: z.string(),
})
export type MoveRebalanceOrdersInput = z.infer<typeof MoveRebalanceOrdersInputSchema>

export const MoveRebalanceOrdersOutputSchema = z.object({
  totalOrdersRequested: z.number().int(),
  totalLegacyOrdersRequested: z.number().int(),
  totalPhoenixOrdersRequested: z.number().int(),
  totalLegacyOrdersUpdated: z.number().int(),
  totalPhoenixOrdersUpdated: z.number().int(),
  totalLegacyOrdersFailed: z.number().int(),
  totalPhoenixOrdersFailed: z.number().int(),
  totalBoxesRequested: z.number().int().nonnegative(),
  totalBoxesUpdated: z.number().int().nonnegative(),
  totalBoxesFailed: z.number().int().nonnegative(),
  legacyOrdersFailed: z.array(RebalanceOrderMoveFailureSchema),
  phoenixOrdersFailed: z.array(RebalanceOrderMoveFailureSchema),
})
export type MoveRebalanceOrdersOutput = z.infer<typeof MoveRebalanceOrdersOutputSchema>

/** HTTP 202 body when an orchestration workflow route uses asyncStart. */
export const WorkflowAsyncStartResponseSchema = z.object({
  workflowId: z.string(),
  status: z.literal('running'),
})
export type WorkflowAsyncStartResponse = z.infer<typeof WorkflowAsyncStartResponseSchema>

export const MoveRebalanceOrdersAsyncStartResponseSchema = WorkflowAsyncStartResponseSchema
export type MoveRebalanceOrdersAsyncStartResponse = WorkflowAsyncStartResponse

export const SendPublicHolidayNotificationsAsyncStartResponseSchema = WorkflowAsyncStartResponseSchema
export type SendPublicHolidayNotificationsAsyncStartResponse = WorkflowAsyncStartResponse

// ─── Send Public Holiday Notifications ──────────────────────────────────────

export const SendPublicHolidayNotificationsInputSchema = z.object({
  operationalBlockId: z
    .number()
    .int()
    .positive()
    .describe('Operational block id from the bulk reschedule run — only orders shifted for this block are notified'),
  holidayName: z.string().describe('Human-readable holiday name for the email (e.g. "Christmas Day")'),
  brand: z
    .nativeEnum(Brand)
    .optional()
    .describe('When set, only notify orders belonging to this brand (e.g. "lyka"). Omit to notify all brands.'),
  blockedDate: z
    .string()
    .date()
    .optional()
    .describe(
      'Optional previous delivery date (YYYY-MM-DD) when multiple blocks share processing — narrows to records moved off this date',
    ),
  userIds: z
    .array(z.string().uuid())
    .min(1)
    .optional()
    .describe(
      'When set, only notify these users (useful for targeted re-sends). Omit entirely to notify all eligible users; an empty array is rejected rather than silently notifying everyone.',
    ),
  dryRun: z
    .boolean()
    .optional()
    .describe(
      'When true, resolve eligibility but do not publish SNS/CIO events. Effective dry-run when this is true OR LaunchDarkly flag public-holiday-notifications-dry-run is true (both must be off to send).',
    ),
  offset: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Pagination cursor — set by continue-as-new; do not pass manually'),
  accumulated: z
    .object({
      totalEligible: z.number().int(),
      sent: z.number().int(),
      skipped: z.number().int(),
      errors: z.number().int(),
      details: z.array(
        z.object({
          userId: z.string(),
          orderId: z.string(),
          status: z.enum(['sent', 'skipped', 'error', 'dry_run']),
          reason: z.string().optional(),
        }),
      ),
      detailsTruncated: z.boolean().optional(),
    })
    .optional()
    .describe('Internal continue-as-new state — do not pass manually'),
})
export type SendPublicHolidayNotificationsInput = z.infer<typeof SendPublicHolidayNotificationsInputSchema>

export const SendPublicHolidayNotificationDetailSchema = z.object({
  userId: z.string(),
  orderId: z.string(),
  status: z.enum(['sent', 'skipped', 'error', 'dry_run']),
  reason: z.string().optional(),
})

export const SendPublicHolidayNotificationsOutputSchema = z.object({
  dryRun: z.boolean(),
  totalEligible: z.number().int(),
  sent: z.number().int(),
  skipped: z.number().int(),
  errors: z.number().int(),
  details: z.array(SendPublicHolidayNotificationDetailSchema),
  detailsTruncated: z.boolean().optional().describe('True when details were capped to bound workflow result size'),
})
export type SendPublicHolidayNotificationsOutput = z.infer<typeof SendPublicHolidayNotificationsOutputSchema>

export const WorkflowJobStatusSchema = z.enum(['running', 'completed', 'failed'])
export type WorkflowJobStatus = z.infer<typeof WorkflowJobStatusSchema>

export const WorkflowStatusErrorSchema = z.object({
  message: z.string(),
  type: z.string().optional(),
})
export type WorkflowStatusError = z.infer<typeof WorkflowStatusErrorSchema>

export const WorkflowStatusResponseSchema = z.object({
  workflowId: z.string(),
  status: WorkflowJobStatusSchema,
  result: z.unknown().optional(),
  error: WorkflowStatusErrorSchema.optional(),
})
export type WorkflowStatusResponse = z.infer<typeof WorkflowStatusResponseSchema>

export const BackfillStarterBoxFeedingPlanInputSchema = z.object({
  userId: z
    .string()
    .uuid()
    .optional()
    .describe('Backfill this Phoenix user only. Omit to scan every live lyka subscription.'),
  batchSize: z
    .number()
    .int()
    .min(1)
    .max(500)
    .optional()
    .default(100)
    .describe('Candidate subscriptions fetched per page. Also caps the exporter lookup, which takes at most 100 ids.'),
  dryRun: z.boolean().default(false).describe('Resolve and count without writing any feeding plans.'),
})

export type BackfillStarterBoxFeedingPlanInput = z.infer<typeof BackfillStarterBoxFeedingPlanInputSchema>

export const BackfillStarterBoxFailureSchema = z.object({
  userId: z.string().uuid(),
  message: z.string(),
})

export type BackfillStarterBoxFailure = z.infer<typeof BackfillStarterBoxFailureSchema>

export const BackfillStarterBoxFeedingPlanOutputSchema = z.object({
  dryRun: z.boolean(),
  /** Live lyka subscriptions examined — every candidate with no ineligible feeding plan. */
  subscriptionsScanned: z.number().int(),
  /** Of those, users whose id maps back to a Laravel user (Phoenix-native signups are skipped). */
  migratedUsersChecked: z.number().int(),
  /** Users given a starter box by this run. */
  usersBackfilled: z.number().int(),
  /** Users the exporter said never had a starter box at signup — no signup_meal_plan, not began_on_trial. */
  usersWithoutStarterBox: z.number().int(),
  /** Users skipped for a reason that needs eyes: first order missing, no live subscription, no matching animal. */
  usersSkipped: z.number().int(),
  feedingPlansCreated: z.number().int(),
  usersFailed: z.array(BackfillStarterBoxFailureSchema),
})

export type BackfillStarterBoxFeedingPlanOutput = z.infer<typeof BackfillStarterBoxFeedingPlanOutputSchema>

export const RepairOrderUserIdOutcomeSchema = z.enum([
  'repaired',
  'wouldRepair',
  'alreadyCorrect',
  'orderNotFound',
  'legacyOrderNotFound',
  'legacyUserNotMapped',
  'failed',
])

export type RepairOrderUserIdOutcome = z.infer<typeof RepairOrderUserIdOutcomeSchema>

export const RepairOrderUserIdResultSchema = z.object({
  legacyOrderId: z.number().int(),
  orderId: z.string().uuid().nullable().describe('Phoenix order id, null when the order could not be found'),
  outcome: RepairOrderUserIdOutcomeSchema,
  previousUserId: z.string().nullable().describe('The user id the order carried before this run'),
  newUserId: z.string().uuid().nullable().describe('The user id the order was (or would have been) given'),
  legacyUserId: z.number().int().nullable().describe('Laravel orders.user_id — the owner Laravel reports'),
  message: z.string().optional().describe('Why the order was skipped or failed'),
})

export type RepairOrderUserIdResult = z.infer<typeof RepairOrderUserIdResultSchema>

export const RepairOrderUserIdsInputSchema = z
  .object({
    legacyOrderId: z.number().int().positive().optional().describe('Repair this one Laravel order id'),
    legacyOrderIds: z
      .array(z.number().int().positive())
      .max(500)
      .optional()
      .describe('Repair these Laravel order ids. Merged with legacyOrderId and de-duplicated; max 500 per run.'),
    dryRun: z
      .boolean()
      .optional()
      .default(false)
      .describe('Resolve the correct owner and report it without writing anything.'),
    verifyExistingUsers: z
      .boolean()
      .optional()
      .default(false)
      .describe(
        'Also check orders whose current user id DOES exist in Phoenix, and repair them if Laravel disagrees. Off by default: the known defect is orders pointing at a user that does not exist, and checking every order costs an exporter + auth round trip each.',
      ),
    batchSize: z
      .number()
      .int()
      .min(1)
      .max(200)
      .optional()
      .default(50)
      .describe('Orders resolved per batch. Bounds the size of each exporter and auth lookup.'),
  })
  .refine((input) => input.legacyOrderId !== undefined || (input.legacyOrderIds?.length ?? 0) > 0, {
    message: 'Provide legacyOrderId or a non-empty legacyOrderIds.',
  })

export type RepairOrderUserIdsInput = z.infer<typeof RepairOrderUserIdsInputSchema>

export const RepairOrderUserIdsOutputSchema = z.object({
  dryRun: z.boolean(),
  totalRequested: z.number().int(),
  repaired: z
    .number()
    .int()
    .describe(
      'Orders patched. On a dry run this counts the orders that WOULD have been patched (outcome `wouldRepair`) — nothing was written.',
    ),
  alreadyCorrect: z.number().int(),
  orderNotFound: z.number().int(),
  legacyOrderNotFound: z.number().int(),
  legacyUserNotMapped: z.number().int(),
  failed: z.number().int(),
  results: z.array(RepairOrderUserIdResultSchema),
})

export type RepairOrderUserIdsOutput = z.infer<typeof RepairOrderUserIdsOutputSchema>


export const SanitizeSubscriptionInputSchema = z.object({
  subscriptionId: z.string().uuid(),
})

export type SanitizeSubscriptionInput = z.infer<typeof SanitizeSubscriptionInputSchema>

export const SanitizeSubscriptionOutputSchema = z.object({
  subscription: SubscriptionSchema,
  /** Ids of the recurring extras that were cancelled. */
  cancelledExtraIds: z.array(z.string().uuid()),
  /** Ids of the feeding-plan SKUs that are unsupported (product or product variant not active). */
  unsupportedFeedingPlanSkuIds: z.array(z.string().uuid()),
})

export type SanitizeSubscriptionOutput = z.infer<typeof SanitizeSubscriptionOutputSchema>

export const RedistributeSubscriptionMealInputSchema = z.object({
  meal: MealQuantitySchema.shape.type.describe('The meal to remove. Its pouches are redistributed onto the plan\'s remaining meals.'),
  userIds: z.array(z.string().uuid()).min(1).max(3500).describe('Customers to update. Only feeding plans whose dog has the given allergen are touched. Large lists rely on workflow continue-as-new.'),
  allergen: z.string().describe('Food allergen slug (e.g. pork). Only that dog\'s feeding plan is updated — sibling dogs on the same subscription are left alone.'),
  dryRun: z.boolean().optional().default(false).describe('Report what would change without writing anything.'),
  emitEvents: z.boolean().optional().default(false).describe('Emit the recipe-updated user event that CCA renders.'),
})

export type RedistributeSubscriptionMealInput = z.infer<typeof RedistributeSubscriptionMealInputSchema>

export const RedistributeSubscriptionMealAffectedUserSchema = z.object({
  userId: z.string().uuid(),
  email: z.string(),
  dogNames: z.array(z.string()).describe('Dogs with the allergen whose feeding plan had the meal removed and redistributed.'),
})

export type RedistributeSubscriptionMealAffectedUser = z.infer<typeof RedistributeSubscriptionMealAffectedUserSchema>

export const RedistributeSubscriptionMealOnlyRecipeSkipSchema = z.object({
  userId: z.string().uuid(),
  email: z.string(),
  dogName: z.string(),
})

export type RedistributeSubscriptionMealOnlyRecipeSkip = z.infer<typeof RedistributeSubscriptionMealOnlyRecipeSkipSchema>

export const RedistributeSubscriptionMealFailedUserSchema = z.object({
  userId: z.string().uuid(),
  email: z.string(),
  message: z.string().describe('Why processing failed for this customer (logged server-side as well).'),
})

export type RedistributeSubscriptionMealFailedUser = z.infer<typeof RedistributeSubscriptionMealFailedUserSchema>

/** Why a feeding plan was left alone. Not an error — most plans in a sweep land here. */
export const RedistributeSubscriptionMealSkipReason = {
  notLykaBrand: 'not_lyka_brand',
  allergenNotPresent: 'allergen_not_present',
  mealNotOnPlan: 'meal_not_on_plan',
  mealIsOnlyRecipe: 'meal_is_only_recipe',
} as const

export type RedistributeSubscriptionMealSkipReason
  = typeof RedistributeSubscriptionMealSkipReason[keyof typeof RedistributeSubscriptionMealSkipReason]

export const RedistributeSubscriptionMealSkipSchema = z.object({
  userId: z.string().uuid(),
  subscriptionId: z.string().uuid(),
  feedingPlanId: z.string().uuid().nullable().describe('Null when the whole subscription was skipped.'),
  reason: z.nativeEnum(RedistributeSubscriptionMealSkipReason),
})

export type RedistributeSubscriptionMealSkip = z.infer<typeof RedistributeSubscriptionMealSkipSchema>

export const RedistributeSubscriptionMealOutputSchema = z.object({
  meal: MealQuantitySchema.shape.type,
  allergen: z.string(),
  dryRun: z.boolean(),
  userIdsRequested: z.number().int().nonnegative(),
  succeeded: z.array(RedistributeSubscriptionMealAffectedUserSchema),
  skippedOnlyRecipe: z.array(RedistributeSubscriptionMealOnlyRecipeSkipSchema),
  failed: z.array(RedistributeSubscriptionMealFailedUserSchema),
})

export type RedistributeSubscriptionMealOutput = z.infer<typeof RedistributeSubscriptionMealOutputSchema>

// --- Remove Subscription Extra From Paid Order (preview + persist) ---

export const RemoveSubscriptionExtraFromPaidOrderInputSchema = z.object({
  workflowId: z.string().optional(),
  orderId: z.string().uuid(),
  extraId: z.string().uuid().describe('The subscription extra ID whose line items should be removed from this order.'),
})

export type RemoveSubscriptionExtraFromPaidOrderInput = z.infer<typeof RemoveSubscriptionExtraFromPaidOrderInputSchema>

const RemovedLineItemDetailSchema = z.object({
  id: z.string().uuid(),
  productId: z.string().uuid(),
  productType: z.nativeEnum(ProductType).optional().nullable(),
  quantity: z.number().int().positive(),
  priceCents: z.number().int().nonnegative(),
})

export type RemovedLineItemDetail = z.infer<typeof RemovedLineItemDetailSchema>

const OrderSummaryForRemovalSchema = z.object({
  orderId: z.string().uuid(),
  subscriptionId: z.string().uuid(),
  paymentStatus: z.string(),
  totalPriceCents: z.number().int().nonnegative(),
  totalChargeCents: z.number().int().nonnegative(),
})

export type OrderSummaryForRemoval = z.infer<typeof OrderSummaryForRemovalSchema>

export const RemoveSubscriptionExtraFromPaidOrderPreviewOutputSchema = z.object({
  order: OrderSummaryForRemovalSchema,
  removedLineItems: z.array(RemovedLineItemDetailSchema),
  originalTotalPriceCents: z.number().int().nonnegative().describe('Pre-discount total before removal (sum of line item prices).'),
  originalTotalChargeCents: z.number().int().nonnegative().describe('What the customer was actually charged (after discount) before removal.'),
  newTotalPriceCents: z.number().int().nonnegative().describe('Pre-discount total without the removed extra.'),
  newTotalChargeCents: z.number().int().nonnegative().describe('What the customer would be charged (after discount) without the removed extra.'),
  refundAmountCents: z.number().describe('Positive = refund to customer; negative = additional charge needed.'),
  discounts: z.array(OrderDiscountSchema).default([]).describe('All discounts currently applied on the order (for debugging/visibility).'),
})

export type RemoveSubscriptionExtraFromPaidOrderPreviewOutput = z.infer<typeof RemoveSubscriptionExtraFromPaidOrderPreviewOutputSchema>

export const RemoveSubscriptionExtraFromPaidOrderPersistOutputSchema = z.object({
  orderId: z.string().uuid(),
  extraId: z.string().uuid(),
  refundAmountCents: z.number().int(),
  originalPlanTotalPriceCents: z.number().int().nonnegative().describe('Meal-plan total on the order before removal should be matched with newPlanTotalPriceCents.'),
  originalPlanTotalChargeCents: z.number().int().nonnegative().describe('Meal-plan charge on the order before removal should be matched with newPlanTotalChargeCents.'),
  newTotalPriceCents: z.number().int().nonnegative(),
  newTotalChargeCents: z.number().int().nonnegative(),
  newPlanTotalPriceCents: z.number().int().nonnegative().describe('Meal-plan portion of the re-priced order (pre-discount), should be matched with originalPlanTotalChargeCents.'),
  newPlanTotalChargeCents: z.number().int().nonnegative().describe('Meal-plan charge portion after discount, should be matched with originalPlanTotalChargeCents.'),
})

export type RemoveSubscriptionExtraFromPaidOrderPersistOutput = z.infer<typeof RemoveSubscriptionExtraFromPaidOrderPersistOutputSchema>
